import {
  AudioSession,
  bounceClipOffline,
  clipOrderSteps,
  cloneInstrument,
  clonePreset,
  createAudioEngine,
  createDefaultDrumKit,
  createDefaultSf2Patch,
  createDefaultExternalMidiPatch,
  createEmptyTelemetry,
  DEFAULT_PRESETS,
  DEFAULT_INSTRUMENT_SLOT_COUNT,
  drumsInstrument,
  expandSceneOrder,
  findPattern,
  findScene,
  createEmptyClip,
  createDefaultInstruments,
  createDefaultSlotMixes,
  createDefaultSlotFxStacks,
  defaultPresetForSlot,
  defaultSlotName,
  emptyCell,
  ensureClipTrackCount,
  instrumentsFromPresets,
  instrumentDisplayName,
  MAX_INSTRUMENT_SLOT_COUNT,
  nextSceneId,
  normalizeClipScenes,
  normalizeSf2Patch,
  normalizeSlotMix,
  normalizeSlotMixes,
  normalizeSlotFxStacks,
  parsePatternString,
  patternSteps,
  patchSlotMix,
  patchSlotFxStack,
  presetsFromInstruments,
  pushSf2BanksForInstruments,
  pushWavetablesForInstruments,
  pushSamplesForInstruments,
  exportMidiFromProject,
  importMidiToProject,
  resizePatternSteps,
  secondsPerStep,
  setSceneOrder,
  sf2Instrument,
  externalMidiInstrument,
  stringifyPattern,
  synthInstrument,
  wavetableInstrument,
  samplerInstrument,
  createDefaultWavetablePreset,
  createDefaultSamplerPreset,
  normalizeSamplerPreset,
  normalizeExternalMidiPatch,
  samplerPresetSampleIds,
  drumKitSampleIds,
  audioBufferToMono,
  interleavedStereoToMono,
  putCachedSample,
  type AudioEngine,
  type ClipIR,
  type ExternalMidiPatch,
  type FxNode,
  type Instrument,
  type InstrumentSlot,
  type KernelMessage,
  type SampleAssetMeta,
  type SynthPreset,
} from "../../../src/lib/audio";
import { ExternalMidiSink } from "./lib/externalMidiSink";
import { LAB_DEMOS, type LabDemoId } from "./lib/demos";
import { labAssetsBaseUrl, labWorkletUrl } from "./lib/labAssets";
import {
  clipFromLabState,
  createLabStateSaver,
  loadLabState,
} from "./lib/labStorage";
import {
  createDefaultMuteSolo,
  migrateLegacyMuteToPatterns,
  normalizeMuteSoloFlags,
  patternMuteFlags,
} from "./lib/muteSolo";
import {
  applyMaterial,
  type MaterialMode,
} from "./lib/material";
import {
  clonePatternAsNew,
  cloneTrackCells,
  createBlankPattern,
  fitCellsToSteps,
  insertPatternAfter,
  nextPatternId,
  reorderPatterns as reorderPatternsInClip,
  moveArrayIndex,
  mapTrackIndexAfterReorder,
  remapTrackControlPath,
  syncLinearArrangement,
  trackHasContent,
} from "./lib/patternHelpers";
import {
  getArrangeClipboard,
  setArrangeClipboard,
} from "./lib/arrangeClipboard";
import {
  cloneInstrumentSlotPayload,
  getInstrumentClipboard,
  setInstrumentClipboard,
  type InstrumentSlotPayload,
} from "./lib/instrumentClipboard";
import { syncArrangeEditCursor, syncArrangePlayhead } from "./lib/arrangePlayhead";
import { clampFxHeight, clampInstrumentWidth, clampPatternsWidth } from "./lib/dockSizes";
import {
  clampPatternLoop,
  patternIdsInLoop,
  patternLoopAfterInsert,
  patternLoopAtIndex,
  patternLoopContainsIndex,
  patternLoopSteps,
  sanitizePatternLoop,
  type PatternLoopRange,
} from "./lib/patternLoop";
import {
  applyBankEntry,
  buildExportAllPayload,
  buildExportEntryPayload,
  deleteBankEntry,
  importBankData,
  initialSlotBankIds,
  renameBankEntry,
  resetSlotFactory,
  saveBankAs,
  saveBankCurrent,
  setBankCategory,
  setBankTags,
  syncSlotBankIdsFromPresets,
  type InstrumentBankResult,
  type InstrumentBankState,
} from "./lib/instrumentBankOps";
import {
  applyWtBankEntry,
  buildWtExportAllPayload,
  buildWtExportEntryPayload,
  deleteWtBankEntry,
  importWtBankData,
  initialWtSlotBankIds,
  renameWtBankEntry,
  saveWtBankAs,
  saveWtBankCurrent,
  setWtBankCategory,
  setWtBankTags,
  syncWtSlotBankIdsFromInstruments,
  type WavetableBankResult,
  type WavetableBankState,
} from "./lib/wavetableBankOps";
import {
  applySamplerBankEntry,
  buildSamplerExportAllPayload,
  buildSamplerExportEntryPayload,
  deleteSamplerBankEntry,
  importSamplerBankData,
  initialSamplerSlotBankIds,
  renameSamplerBankEntry,
  saveSamplerBankAs,
  saveSamplerBankCurrent,
  setSamplerBankCategory as setSamplerBankEntryCategory,
  setSamplerBankTags as setSamplerBankEntryTags,
  syncSamplerSlotBankIdsFromInstruments,
  type SamplerBankResult,
  type SamplerBankState,
} from "./lib/samplerBankOps";
import {
  buildLabProject,
  downloadBytes,
  downloadJson,
  downloadMidi,
  parseLabProject,
  pickAdamProjectFile,
  pickJsonFile,
  pickMidiFile,
} from "./lib/projectFile";
import { assignSamplesToDrumPads } from "./lib/drumPadAssign";
import { listSamplerPadRegions } from "./lib/samplerSlices";
import {
  deserializeAdamPackage,
  looksLikeZip,
  serializeAdamPackage,
} from "./lib/adamPackage";
import {
  deserializeAdamSamplesPackage,
  pickAdamSamplesFile,
  serializeAdamSamplesPackage,
} from "./lib/adamsamplesPackage";
import {
  collectFoldersForExport,
  collectSamplesForExport,
  createSampleFolderSync,
  forgetSampleAssetSync,
  forgetSampleFolderSync,
  getSampleAssetMeta,
  getSampleFolderMeta,
  ingestFoldersFromPackage,
  ingestSampleFromPackage,
  ingestSamplePcm,
  listImportedSampleAssets,
  listSampleFolders,
  moveSampleFolderSync,
  moveSampleToFolderSync,
  renameSampleAssetSync,
  renameSampleFolderSync,
  resolveSampleRuntime,
  restoreSampleAssetSync,
  restoreSampleFolderSync,
  wipeSampleLibrary as wipeSampleLibraryCatalog,
  captureDeletedFolderPayload,
  ensureSampleLibraryReady,
} from "./lib/sampleLibrary";
import {
  ADAM_FILE_EXTENSION,
  ADAM_MIME_TYPE,
  ADAM_SAMPLES_FILE_EXTENSION,
  ADAM_SAMPLES_MIME_TYPE,
} from "./lib/fileTypes";
import { strFromU8 } from "fflate";
import { bounceStemsToZip } from "./lib/stems";
import {
  clampRowMarkerColor,
  normalizeTrackColors,
} from "./lib/trackColors";
import { downloadWav, encodeWavPcm16 } from "./lib/wav";
import { startMicCapture, type MicRecorder } from "./lib/micCapture";
import { startBusCapture, type BusRecorder } from "./lib/busCapture";
import {
  clearTransportRecordMeter,
  setTransportRecordMeterHost,
} from "./lib/transportRecordMeter";

import { setLabActions } from "./labActions";
import {
  createDefaultDrumBank,
  mergeDrumBanksPreservingUser,
  sanitizeDrumBank,
} from "./lib/drumKitBank";
import { mergeBanksPreservingUser } from "./lib/presetBank";
import {
  createDefaultSamplerBank,
  mergeSamplerBanksPreservingUser,
  sanitizeSamplerBank,
} from "./lib/samplerBank";
import {
  createDefaultWavetableBank,
  mergeWavetableBanksPreservingUser,
  sanitizeWavetableBank,
} from "./lib/wavetableBank";
import { buildPerformanceClip } from "./lib/performanceClip";
import {
  controlSessionStore,
  setControlBindings,
  setControlBindingsPersistence,
  startControlSession,
  stopControlSession,
} from "./controls/controlSession";
import {
  setMidiAuditionSlotGetter,
  setMidiAuditionVoiceModeGetter,
  startMidiNoteAudition,
  stopMidiNoteAudition,
} from "./controls/midiNoteAudition";
import {
  patchPerformance,
  performanceStore,
  reorderPerformanceLanes,
  resizePerformanceLanes,
  type PerformancePending,
} from "./store/performanceStore";
import {
  patchProject,
  patchTransport,
  patchUi,
  projectStore,
  setStatusMessage,
  transportStore,
  uiStore,
} from "./store";
import { setTrackerGridCallbacks } from "./tracker/trackerGridCallbacks";
import { getTrackerGrid } from "./tracker/trackerGridRegistry";
import {
  AddTrackCommand,
  DuplicateTrackCommand,
  PasteInstrumentCommand,
  RemoveTrackCommand,
  ReorderTracksCommand,
  SetPatternMuteCommand,
  SetSoloCommand,
  SetTrackColorCommand,
  SetMasterFxCommand,
  SetSlotFxCommand,
  SetSlotMixCommand,
  RenamePatternCommand,
  CreatePatternCommand,
  DeletePatternCommand,
  ReorderPatternsCommand,
  PasteTrackCellsCommand,
  ResizePatternCommand,
  SetPatternLoopCommand,
  SetBpmCommand,
  SetStepDivisionCommand,
  SetClipLoopCommand,
  SetInstrumentCommand,
  SetSynthBankCommand,
  SetWavetableBankCommand,
  SetSamplerBankCommand,
  SetDrumBankCommand,
  AdamTransactionCommand,
  clearAdamSessionHistory,
  getAdamCommandHost,
  adamUndo,
  adamRedo,
  registerAdamProjectBridge,
  registerAdamArrangeBridge,
  registerAdamInstrumentBridge,
  registerAdamSampleBridge,
  RenameSampleCommand,
  DeleteSampleCommand,
  MoveSampleCommand,
  CreateFolderCommand,
  RenameFolderCommand,
  MoveFolderCommand,
  DeleteFolderCommand,
  type RemovedTrackPayload,
  type RemovedPatternPayload,
  type SynthBankSnapshot,
  type WavetableBankSnapshot,
  type SamplerBankSnapshot,
  type DeletedSamplePayload,
  type DeletedFolderPayload,
  loopEqual,
} from "./commands";
import { fxEqual, mixEqual } from "./commands/fxMixCommands";

export type LabRuntimeHandle = {
  dispose: () => void;
};

let activeRuntime: LabRuntimeHandle | null = null;

/**
 * Bind the lab controller to the React-rendered shell.
 * Call after TrackerGrid registers. Dispose on unmount so HMR remounts rebind
 * to the new grid handle (also makes a StrictMode remount cycle safe).
 */
export function startLabRuntime(): LabRuntimeHandle {
  if (activeRuntime) {
    activeRuntime.dispose();
  }

  const LIVE = "live";

  const tracker = getTrackerGrid();
  if (!tracker) {
    throw new Error("Adam: TrackerGrid not mounted — LabBody must render before startLabRuntime");
  }

  const saved = loadLabState();
  const persist = createLabStateSaver();
  const savedSolo = normalizeMuteSoloFlags(saved.solo);

  let engine: AudioEngine | undefined;
  let session: AudioSession | undefined;
  const externalMidiSink = new ExternalMidiSink({
    getInstrument: slot => projectStore.getValue().instruments[slot],
    getAudioTime: () => engine?.getCurrentTime() ?? 0,
  });
  void externalMidiSink.ensureAccess();

  function handleMusicKernelMessage(message: KernelMessage): void {
    switch (message.type) {
      case "noteOn":
        externalMidiSink.noteOn(
          message.slot,
          message.note,
          message.velocity,
          message.time,
          message.noteFx,
        );
        break;
      case "noteOff":
        externalMidiSink.noteOff(message.slot, message.note ?? 0, message.time);
        break;
      case "allNotesOff":
        externalMidiSink.allNotesOff();
        break;
      default:
        break;
    }
  }

  let clip: ClipIR = clipFromLabState(saved);
  syncLinearArrangement(clip);
  // Legacy project-global muted[] → per-pattern track.muted (once).
  const migratedMute = migrateLegacyMuteToPatterns(clip, saved.muted);
  let syncing = false;
  const bootInstruments = saved.instruments ?? instrumentsFromPresets(saved.presets);
  const bootTrackCount = bootInstruments.length || DEFAULT_INSTRUMENT_SLOT_COUNT;
  const bootActivePatternId = saved.panels?.activePatternId ?? "P0";
  const bootMuted = patternMuteFlags(clip, bootActivePatternId, bootTrackCount);
  const bootTrackColors = normalizeTrackColors(saved.trackColors, bootTrackCount);
  const bootPatternLoop = sanitizePatternLoop(
    saved.panels?.patternLoop,
    clip.patterns.length,
  );

  resizePerformanceLanes(bootTrackCount);

  patchProject({
    clip,
    instruments: bootInstruments,
    presets: saved.presets,
    bank: saved.bank,
    drumBank: saved.drumBank ?? createDefaultDrumBank(),
    wavetableBank: sanitizeWavetableBank(saved.wavetableBank ?? createDefaultWavetableBank()),
    samplerBank: sanitizeSamplerBank(saved.samplerBank ?? createDefaultSamplerBank()),
    slotMix: normalizeSlotMixes(saved.slotMix, bootTrackCount),
    slotFx: normalizeSlotFxStacks(saved.slotFx, bootTrackCount),
    muted: bootMuted,
    solo: savedSolo,
    trackColors: bootTrackColors,
    fx: saved.fx,
  });
  patchTransport({
    bpm: clip.bpm,
    stepDivision: clip.stepDivision,
    patternSteps: patternSteps(findPattern(clip, bootActivePatternId) ?? clip.patterns[0]),
    loop: clip.loop,
    followPlayhead: Boolean(saved.panels?.followPlayhead),
    armMode: Boolean(saved.panels?.armMode),
  });
  patchUi({
    statusOpen: Boolean(saved.panels?.statusOpen),
    dslOpen: Boolean(saved.panels?.dslOpen),
    dslDraft: stringifyPattern(clip),
    dslWarnings: "",
    fxOpen: Boolean(saved.panels?.fxOpen),
    fxHeight: clampFxHeight(saved.panels?.fxHeight),
    patternsOpen: Boolean(saved.panels?.patternsOpen),
    patternsWidth: clampPatternsWidth(saved.panels?.patternsWidth),
    samplesOpen: Boolean(saved.panels?.samplesOpen),
    instrumentSlot: saved.panels?.instrumentSlot ?? saved.panels?.synthSlot ?? null,
    instrumentWidth: clampInstrumentWidth(saved.panels?.instrumentWidth),
    material: saved.material,
    activePatternId: bootActivePatternId,
    patternLoop: bootPatternLoop,
    activeSceneId:
      typeof saved.panels?.activeSceneId === "string" && clip.scenes.some(s => s.id === saved.panels!.activeSceneId)
        ? saved.panels.activeSceneId
        : (clip.scenes[0]?.id ?? "S0"),
    trackMuted: bootMuted,
    trackSolo: savedSolo,
  });
  tracker.setMuteSolo(bootMuted, savedSolo, { emit: false });
  tracker.setTrackColors(bootTrackColors);

  let slotBankIds = syncSlotBankIdsFromPresets({
    presets: saved.presets,
    bank: saved.bank,
    slotBankIds: initialSlotBankIds(),
  }).slotBankIds;

  let wtSlotBankIds = syncWtSlotBankIdsFromInstruments({
    instruments: saved.instruments ?? [],
    bank: saved.wavetableBank ?? [],
    slotBankIds: initialWtSlotBankIds((saved.instruments ?? saved.presets).length),
  }).slotBankIds;

  let samplerSlotBankIds = syncSamplerSlotBankIdsFromInstruments({
    instruments: saved.instruments ?? [],
    bank: saved.samplerBank ?? [],
    slotBankIds: initialSamplerSlotBankIds((saved.instruments ?? saved.presets).length),
  }).slotBankIds;

  function bankState(): InstrumentBankState {
    const { presets, bank } = projectStore.getValue();
    return { presets, bank, slotBankIds };
  }

  function wtBankState(): WavetableBankState {
    const { instruments, wavetableBank } = projectStore.getValue();
    while (wtSlotBankIds.length < instruments.length) {
      wtSlotBankIds.push(undefined);
    }
    return { instruments, bank: wavetableBank, slotBankIds: wtSlotBankIds };
  }

  function samplerBankState(): SamplerBankState {
    const { instruments, samplerBank } = projectStore.getValue();
    while (samplerSlotBankIds.length < instruments.length) {
      samplerSlotBankIds.push(undefined);
    }
    return { instruments, bank: samplerBank, slotBankIds: samplerSlotBankIds };
  }

  function captureSynthBankSnapshot(): SynthBankSnapshot {
    const { presets, bank, instruments } = projectStore.getValue();
    return {
      presets: presets.map(clonePreset),
      bank: structuredClone(bank),
      slotBankIds: [...slotBankIds],
      instruments: instruments.map(cloneInstrument),
    };
  }

  function applySynthBankSnapshotLive(
    snapshot: SynthBankSnapshot,
    changedSlots: InstrumentSlot[],
  ): void {
    slotBankIds = [...snapshot.slotBankIds];
    const instruments = snapshot.instruments.map(cloneInstrument);
    const presets = snapshot.presets.map(clonePreset);
    const bank = structuredClone(snapshot.bank);
    patchProject({ instruments, presets, bank });
    for (const slot of changedSlots) {
      const instrument = instruments[slot];
      if (instrument) {
        void pushInstrumentToEngine(slot, instrument);
      }
    }
    persist.schedule({ instruments, presets, bank });
  }

  function commitBankResult(result: InstrumentBankResult): void {
    const from = captureSynthBankSnapshot();
    const { instruments: current } = projectStore.getValue();
    // Bank ops only apply to synth slots — drum kits stay put.
    const instruments = current.map((inst, i) => {
      if (!result.changedSlots.includes(i as InstrumentSlot)) {
        return cloneInstrument(inst);
      }
      if (inst.kind === "drums" || inst.kind === "sf2" || inst.kind === "wavetable" || inst.kind === "sampler") {
        return cloneInstrument(inst);
      }
      return synthInstrument(result.presets[i]!);
    });
    const to: SynthBankSnapshot = {
      presets: result.presets.map(clonePreset),
      bank: structuredClone(result.bank),
      slotBankIds: [...result.slotBankIds],
      instruments,
    };
    getAdamCommandHost().execute(
      new SetSynthBankCommand(from, to, result.changedSlots, { label: result.status }),
    );
    setStatus(result.status);
  }

  function captureWtBankSnapshot(): WavetableBankSnapshot {
    const { instruments, wavetableBank } = projectStore.getValue();
    while (wtSlotBankIds.length < instruments.length) {
      wtSlotBankIds.push(undefined);
    }
    return {
      instruments: instruments.map(cloneInstrument),
      bank: structuredClone(wavetableBank),
      slotBankIds: [...wtSlotBankIds],
    };
  }

  function applyWtBankSnapshotLive(
    snapshot: WavetableBankSnapshot,
    changedSlots: InstrumentSlot[],
  ): void {
    wtSlotBankIds = [...snapshot.slotBankIds];
    const instruments = snapshot.instruments.map(cloneInstrument);
    const bank = structuredClone(snapshot.bank);
    patchProject({ instruments, wavetableBank: bank });
    for (const slot of changedSlots) {
      const instrument = instruments[slot];
      if (instrument?.kind === "wavetable") {
        void pushInstrumentToEngine(slot, instrument);
      }
    }
    persist.schedule({ instruments, wavetableBank: bank });
  }

  function commitWtBankResult(result: WavetableBankResult): void {
    const from = captureWtBankSnapshot();
    const to: WavetableBankSnapshot = {
      instruments: result.instruments.map(cloneInstrument),
      bank: structuredClone(result.bank),
      slotBankIds: [...result.slotBankIds],
    };
    getAdamCommandHost().execute(
      new SetWavetableBankCommand(from, to, result.changedSlots, { label: result.status }),
    );
    setStatus(result.status);
  }

  function captureSamplerBankSnapshot(): SamplerBankSnapshot {
    const { instruments, samplerBank } = projectStore.getValue();
    while (samplerSlotBankIds.length < instruments.length) {
      samplerSlotBankIds.push(undefined);
    }
    return {
      instruments: instruments.map(cloneInstrument),
      bank: structuredClone(samplerBank),
      slotBankIds: [...samplerSlotBankIds],
    };
  }

  function applySamplerBankSnapshotLive(
    snapshot: SamplerBankSnapshot,
    changedSlots: InstrumentSlot[],
  ): void {
    samplerSlotBankIds = [...snapshot.slotBankIds];
    const instruments = snapshot.instruments.map(cloneInstrument);
    const bank = structuredClone(snapshot.bank);
    patchProject({ instruments, samplerBank: bank });
    for (const slot of changedSlots) {
      const instrument = instruments[slot];
      if (instrument?.kind === "sampler") {
        void pushInstrumentToEngine(slot, instrument);
      }
    }
    persist.schedule({ instruments, samplerBank: bank });
  }

  function commitSamplerBankResult(result: SamplerBankResult): void {
    const from = captureSamplerBankSnapshot();
    const to: SamplerBankSnapshot = {
      instruments: result.instruments.map(cloneInstrument),
      bank: structuredClone(result.bank),
      slotBankIds: [...result.slotBankIds],
    };
    getAdamCommandHost().execute(
      new SetSamplerBankCommand(from, to, result.changedSlots, { label: result.status }),
    );
    setStatus(result.status);
  }

  function pushAllInstruments(instruments: Instrument[]): void {
    if (!engine) {
      return;
    }
    for (let slot = 0; slot < instruments.length; slot++) {
      const instrument = instruments[slot]
        ?? synthInstrument(defaultPresetForSlot(slot));
      engine.transport.setInstrument(slot as InstrumentSlot, instrument);
    }
  }

  async function ensureSf2Banks(instruments: Instrument[]): Promise<void> {
    if (!engine) {
      return;
    }
    await pushSf2BanksForInstruments(engine, instruments);
  }

  async function pushInstrumentToEngine(slot: InstrumentSlot, instrument: Instrument): Promise<void> {
    if (!engine) {
      return;
    }
    if (instrument.kind === "sf2") {
      try {
        setStatus(`Loading soundfont…`);
        await ensureSf2Banks([instrument]);
      } catch (error) {
        setStatus(`SoundFont load failed: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
    }
    if (instrument.kind === "wavetable") {
      try {
        await pushWavetablesForInstruments(engine, [instrument], labAssetsBaseUrl());
      } catch (error) {
        setStatus(`Wavetable load failed: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
    }
    if (instrument.kind === "sampler" || instrument.kind === "drums") {
      try {
        await pushSamplesForInstruments(engine, [instrument], resolveSampleRuntime);
      } catch (error) {
        setStatus(`Sample load failed: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
    }
    engine.transport.setInstrument(slot, instrument);
  }

  async function ingestDecodedSampler(
    slot: InstrumentSlot,
    options: {
      name: string;
      pcm: Float32Array;
      sampleRate: number;
      source?: "import" | "record" | "bounce";
    },
  ): Promise<void> {
    if (!engine) {
      setStatus("Audio engine not ready");
      return;
    }
    const instrument = projectStore.getValue().instruments[slot];
    if (instrument?.kind !== "sampler") {
      setStatus("Switch slot to Sampler to load audio");
      return;
    }
    const meta = await ingestSamplePcm({
      name: options.name,
      pcm: options.pcm,
      sampleRate: options.sampleRate,
      source: options.source ?? "import",
    });
    putCachedSample({
      id: meta.id,
      name: meta.name,
      sampleRate: meta.sampleRate,
      pcm: options.pcm,
      rootKey: meta.rootKey,
    });
    const preset = normalizeSamplerPreset({
      ...instrument.preset,
      name: meta.name,
      primarySampleId: meta.id,
      loop: { ...instrument.preset.loop, enabled: false },
      // Instrument-level region drives single-zone playback; omit zone.region
      // so Author drag-select updates what notes hear.
      region: { start: 0, end: options.pcm.length },
      slices: undefined,
      zones: instrument.preset.zones.map((zone, i) =>
        i === 0
          ? (() => {
              const { region: _drop, ...rest } = zone;
              return {
                ...rest,
                sampleId: meta.id,
                rootKey: meta.rootKey ?? 60,
              };
            })()
          : zone,
      ),
    });
    commitInstrument(slot, samplerInstrument(preset));
    await pushSamplesForInstruments(engine, [samplerInstrument(preset)], resolveSampleRuntime);
    setStatus(
      options.source === "record"
        ? `Recorded sample “${meta.name}”`
        : options.source === "bounce"
        ? `Bounced sample “${meta.name}”`
        : `Imported sample “${meta.name}”`,
    );
  }

  function applyInstrumentLive(slot: InstrumentSlot, instrument: Instrument): void {
    const instruments = projectStore.getValue().instruments.map((inst, i) =>
      i === slot ? cloneInstrument(instrument) : inst,
    );
    patchProject({ instruments });
    void pushInstrumentToEngine(slot, instrument).then(() => {
      setStatus(`Updated ${instrumentDisplayName(instrument, slot)} (slot ${slot + 1})`);
    });
    persist.schedule({ instruments });
  }

  function commitInstrument(slot: InstrumentSlot, instrument: Instrument): void {
    const current = projectStore.getValue().instruments[slot];
    if (!current) {
      applyInstrumentLive(slot, instrument);
      return;
    }
    getAdamCommandHost().execute(
      new SetInstrumentCommand(
        slot,
        cloneInstrument(current),
        cloneInstrument(instrument),
      ),
    );
  }

  function setStatus(message: string): void {
    setStatusMessage(message);
  }

  function setStatusOpen(open: boolean, options?: { persist?: boolean }): void {
    patchUi({ statusOpen: open });
    if (options?.persist !== false) {
      persist.schedule({ panels: { statusOpen: open } });
    }
  }

  function formatMmSs(seconds: number): string {
    const total = Math.max(0, Math.floor(seconds + 1e-9));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  let lastPlayhead: { patternId: string; row: number } | undefined;
  /** While music is playing, live clip updates stay scoped to this mode. */
  type PlayScope = "pattern" | "scene" | "project";
  let playingScope: PlayScope | null = null;
  /** Ableton-style session grid — mutually exclusive with regular play scopes. */
  let performancePlaying = false;
  let performanceLanePeriods: number[] = [];
  let performanceLastRow: number | null = null;
  let performanceBus: BusRecorder | null = null;
  let activeSceneId =
    typeof saved.panels?.activeSceneId === "string" && clip.scenes.some(s => s.id === saved.panels!.activeSceneId)
      ? saved.panels.activeSceneId
      : (clip.scenes[0]?.id ?? "S0");

  function syncArrangeCursorFromTracker(): void {
    const ref = tracker.locateViewStep(tracker.getCursorStep());
    syncArrangeEditCursor({
      patternId: ref?.patternId ?? tracker.getActivePatternId(),
      row: ref?.localStep ?? tracker.getCursorStep(),
    });
  }

  function currentPatternLoop(next: ClipIR = clip): PatternLoopRange {
    return clampPatternLoop(uiStore.getValue().patternLoop, next.patterns.length);
  }

  function applyPatternLoop(
    range: PatternLoopRange,
    options?: { persist?: boolean; syncTracker?: boolean },
  ): PatternLoopRange {
    const next = clampPatternLoop(range, clip.patterns.length);
    const prev = uiStore.getValue().patternLoop;
    if (prev.start === next.start && prev.length === next.length) {
      if (options?.syncTracker !== false) {
        tracker.setViewPatternIds(patternIdsInLoop(clip.patterns, next));
      }
      return next;
    }
    patchUi({ patternLoop: next });
    if (options?.persist !== false) {
      persist.schedule({ panels: { patternLoop: next } });
    }
    if (options?.syncTracker !== false) {
      tracker.setViewPatternIds(patternIdsInLoop(clip.patterns, next));
    }
    syncArrangeCursorFromTracker();
    updateTimeDisplay();
    return next;
  }

  function updateTimeDisplay(): void {
    const stepSec = secondsPerStep(clip);
    const loop = currentPatternLoop();
    const rangeStepsCount = patternLoopSteps(clip.patterns, loop);
    const compositionSteps = clipOrderSteps(clip);
    const playing = session?.isMusicPlaying() ?? false;
    let absolute = Math.max(0, tracker.getCursorStep());
    if (playing && lastPlayhead) {
      absolute = tracker.locateGlobalStep(lastPlayhead.patternId, lastPlayhead.row)
        ?? lastPlayhead.row;
    }
    const currentSec = Math.max(0, absolute) * stepSec;
    const patternSec = rangeStepsCount * stepSec;
    const compositionSec = compositionSteps * stepSec;
    const timeCurrent = formatMmSs(currentSec);
    const timePattern = formatMmSs(patternSec);
    const timeComposition = formatMmSs(compositionSec);
    const timeTitle = [
      playing ? `Playback ${timeCurrent}` : `Cursor ${timeCurrent}`,
      `selected ${timePattern}`,
      `composition ${timeComposition}`,
    ].join(" · ");
    const activeSteps = activePatternSteps();
    const prev = transportStore.getValue();
    // Avoid React chrome re-renders when the mm:ss clock hasn't changed.
    if (
      prev.timeCurrent === timeCurrent
      && prev.timePattern === timePattern
      && prev.timeComposition === timeComposition
      && prev.timeTitle === timeTitle
      && prev.patternSteps === activeSteps
    ) {
      return;
    }
    patchTransport({
      timeCurrent,
      timePattern,
      timeComposition,
      timeTitle,
      patternSteps: activeSteps,
    });
  }

  function activePatternSteps(next: ClipIR = clip): number {
    return patternSteps(findPattern(next, tracker.getActivePatternId()));
  }

  /** Clip view for transport: loop range, one scene, or the full song. */
  function scopedPlayClip(full: ClipIR, scope: PlayScope): ClipIR {
    const clone = structuredClone(full);
    normalizeClipScenes(clone);
    if (scope === "project") {
      return clone;
    }
    if (scope === "scene") {
      const scene = findScene(clone, activeSceneId);
      if (!scene) {
        return clone;
      }
      return {
        bpm: clone.bpm,
        stepDivision: clone.stepDivision,
        loop: clone.loop,
        patterns: clone.patterns,
        scenes: [{ id: scene.id, ...(scene.name ? { name: scene.name } : {}), order: [...scene.order] }],
        order: [scene.id],
      };
    }
    // "pattern" and "range" both use the arrange loop brace selection.
    const loop = clampPatternLoop(uiStore.getValue().patternLoop, clone.patterns.length);
    const order = patternIdsInLoop(clone.patterns, loop);
    if (order.length === 0) {
      return clone;
    }
    return {
      bpm: clone.bpm,
      stepDivision: clone.stepDivision,
      loop: clone.loop,
      patterns: clone.patterns,
      scenes: [{ id: "S0", order: [...order] }],
      order: ["S0"],
    };
  }

  function syncStepsField(): void {
    patchTransport({ patternSteps: activePatternSteps() });
  }

  let lastInstrumentSlot: InstrumentSlot =
    saved.panels?.instrumentSlot ?? saved.panels?.synthSlot ?? 0;

  /** Keep left dock (when open) on the tracker cursor's instrument column. */
  function followCursorInstrumentSlot(track: InstrumentSlot): void {
    lastInstrumentSlot = track;
    if (
      uiStore.getValue().instrumentSlot != null
      && uiStore.getValue().instrumentSlot !== track
    ) {
      patchUi({ instrumentSlot: track });
      persist.schedule({ panels: { instrumentSlot: track, synthSlot: track } });
    }
  }

  function setDslOpen(open: boolean, options?: { persist?: boolean }): void {
    if (open) {
      patchUi({ dslOpen: open, dslDraft: stringifyPattern(clip) });
    } else {
      patchUi({ dslOpen: open });
    }
    if (options?.persist !== false) {
      persist.schedule({ panels: { dslOpen: open } });
    }
  }

  setTrackerGridCallbacks({
    onChange(next) {
      if (syncing) {
        return;
      }
      clip = next;
      syncStepsField();
      syncLoopButton();
      syncSynthSlotLabels(clip);
      patchTransport({ bpm: clip.bpm, stepDivision: clip.stepDivision, loop: clip.loop });
      patchProject({ clip });
      if (uiStore.getValue().dslOpen) {
        patchUi({ dslDraft: stringifyPattern(clip) });
      }
      syncLiveClip(clip);
      persist.schedule({ clip, panels: { activePatternId: tracker.getActivePatternId() } });
      updateTimeDisplay();
      syncArrangeCursorFromTracker();
    },
    onMuteSoloChange(state) {
      // Mute lives on pattern tracks; keep the engine global mute clear so
      // PatternPlayer reads per-pattern flags — except during Performance,
      // where session mute mirrors the UI (PERF clip has its own mutes).
      syncSessionMuteSolo(
        state.solo,
        state.solo.length,
        performancePlaying ? state.muted : undefined,
      );
      patchUi({ trackMuted: state.muted, trackSolo: state.solo });
      patchProject({ muted: state.muted, solo: state.solo });
      persist.schedule({ muted: state.muted, solo: state.solo });
      const solos = state.solo.filter(Boolean).length;
      const mutes = state.muted.filter(Boolean).length;
      if (solos) {
        setStatus(`Solo · ${solos} track${solos === 1 ? "" : "s"}`);
      } else if (mutes) {
        setStatus(`Mute · ${mutes} track${mutes === 1 ? "" : "s"}`);
      } else {
        setStatus("All tracks audible");
      }
    },
    onAuditionNote(track, midi, on) {
      if (on) {
        engine?.transport.noteOn(track as InstrumentSlot, midi, 0.85);
      } else {
        engine?.transport.noteOff(track as InstrumentSlot, midi);
      }
    },
    onPositionChange() {
      updateTimeDisplay();
      followCursorInstrumentSlot(tracker.getCursorTrack() as InstrumentSlot);
      syncArrangeCursorFromTracker();
    },
    onActivePatternChange(patternId) {
      syncStepsField();
      patchUi({ activePatternId: patternId });
      persist.schedule({ panels: { activePatternId: patternId } });
      updateTimeDisplay();
      syncArrangeCursorFromTracker();
    },
  });

  syncArrangeCursorFromTracker();

  function setArmed(armed: boolean): void {
    patchTransport({ armed });
  }

  let bouncing = false;

  function setBounceProgress(ratio: number): void {
    const progress = Math.max(0, Math.min(1, ratio));
    const prev = uiStore.getValue().bounce;
    if (!prev.active) {
      return;
    }
    patchUi({ bounce: { ...prev, progress } });
  }

  function setBouncing(active: boolean, title = "Bouncing to WAV…"): void {
    bouncing = active;
    patchUi({
      bounce: {
        active,
        title: active ? title : uiStore.getValue().bounce.title,
        progress: active ? 0 : uiStore.getValue().bounce.progress,
      },
    });
  }

  async function pushAllInstrumentsAsync(instruments: Instrument[]): Promise<void> {
    if (!engine) {
      return;
    }
    try {
      await ensureSf2Banks(instruments);
      await pushWavetablesForInstruments(engine, instruments, labAssetsBaseUrl());
      await pushSamplesForInstruments(engine, instruments, resolveSampleRuntime);
    } catch (error) {
      setStatus(`Instrument assets: ${error instanceof Error ? error.message : String(error)}`);
    }
    pushAllInstruments(instruments);
  }

  function pushAllPresets(presets: SynthPreset[]): void {
    void pushAllInstrumentsAsync(instrumentsFromPresets(presets));
  }

  function syncLiveClip(next: ClipIR, scopeOverride?: PlayScope | null): void {
    if (!session) {
      return;
    }
    if (performancePlaying) {
      pushPerformanceAssembly(next);
      return;
    }
    const scope = scopeOverride !== undefined ? scopeOverride : playingScope;
    const forTransport = scope ? scopedPlayClip(next, scope) : next;
    if (session.getClip(LIVE)) {
      session.updateSound(LIVE, forTransport);
    } else {
      session.registerSound(LIVE, forTransport);
    }
  }

  function pushPerformanceAssembly(source: ClipIR = clip): {
    quantumSteps: number;
    lanePeriods: number[];
  } {
    const trackCount = Math.max(
      1,
      projectStore.getValue().instruments.length,
      source.patterns[0]?.tracks.length ?? 0,
    );
    resizePerformanceLanes(trackCount);
    const laneIds = performanceStore.getValue().lanes.map(lane => lane.activePatternId);
    const built = buildPerformanceClip(source, laneIds);
    performanceLanePeriods = built.lanePeriods;
    patchPerformance({ quantumSteps: built.quantumSteps });
    if (session) {
      if (session.getClip(LIVE)) {
        session.updateSound(LIVE, built.clip);
      } else {
        session.registerSound(LIVE, built.clip);
      }
    }
    return { quantumSteps: built.quantumSteps, lanePeriods: built.lanePeriods };
  }

  function crossedPerformanceLaneBoundary(
    prev: number,
    row: number,
    period: number,
  ): boolean {
    if (row < prev) {
      // Quantum wrap — every lane period ends on the assembled clip boundary.
      return true;
    }
    const p = Math.max(1, Math.floor(period));
    return Math.floor(prev / p) !== Math.floor(row / p);
  }

  function performancePendingPeriod(
    lane: { activePatternId: string | null; pending: PerformancePending | null },
    laneIndex: number,
  ): number {
    if (lane.pending?.kind === "launch") {
      // Idle → launch must wait for the *incoming* pattern's period so new
      // tracks sync with material already looping that length.
      return Math.max(1, patternSteps(findPattern(clip, lane.pending.patternId)));
    }
    if (lane.activePatternId) {
      return Math.max(
        1,
        performanceLanePeriods[laneIndex]
          ?? patternSteps(findPattern(clip, lane.activePatternId)),
      );
    }
    return Math.max(1, performanceStore.getValue().quantumSteps);
  }

  function cutPerformanceSlots(slots: Iterable<number>): void {
    if (!engine) {
      return;
    }
    for (const slot of slots) {
      if (slot < 0 || slot >= MAX_INSTRUMENT_SLOT_COUNT) {
        continue;
      }
      // C-- (open hold) never schedules an auto gate — mute alone can leave the
      // voice hanging, especially when solo overrides pattern mute. Hard-cut
      // only the lanes that actually stopped.
      engine.transport.noteOff(slot as InstrumentSlot);
    }
  }

  function flushPerformancePendings(prevRow: number, row: number): void {
    const state = performanceStore.getValue();
    let changed = false;
    const stoppedSlots: number[] = [];
    const lanes = state.lanes.map(lane => ({
      activePatternId: lane.activePatternId,
      pending: lane.pending ? { ...lane.pending } : null,
    }));
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i]!;
      if (!lane.pending) {
        continue;
      }
      const period = performancePendingPeriod(lane, i);
      if (!crossedPerformanceLaneBoundary(prevRow, row, period)) {
        continue;
      }
      if (lane.pending.kind === "stop") {
        if (lane.activePatternId != null) {
          stoppedSlots.push(i);
        }
        lane.activePatternId = null;
      } else {
        lane.activePatternId = lane.pending.patternId;
      }
      lane.pending = null;
      changed = true;
    }
    if (!changed) {
      return;
    }
    patchPerformance({ lanes });
    pushPerformanceAssembly();
    cutPerformanceSlots(stoppedSlots);
  }

  function onPerformancePlayhead(row: number): void {
    const prev = performanceLastRow;
    performanceLastRow = row;
    patchPerformance({ playheadRow: row });
    if (prev === null || prev === row) {
      return;
    }
    flushPerformancePendings(prev, row);
  }

  /**
   * Push solo (always) + mute mask.
   * Outside Performance, mute stays clear so PatternPlayer reads per-pattern
   * `track.muted`. During Performance the assembled PERF clip ignores arrange
   * mutes, so we mirror UI mute into the session mask.
   */
  function syncSessionMuteSolo(
    solo: boolean[],
    trackCount = solo.length,
    muted?: boolean[],
  ): void {
    const n = trackCount || DEFAULT_INSTRUMENT_SLOT_COUNT;
    const useMute = muted ?? (performancePlaying ? tracker.getMuted() : undefined);
    session?.setMusicMuteSolo({
      muted: useMute
        ? normalizeMuteSoloFlags(useMute, n)
        : createDefaultMuteSolo(n),
      solo: normalizeMuteSoloFlags(solo, n),
    });
  }

  /**
   * Adopt mute/solo after load/import: migrate legacy global mute onto patterns,
   * mirror active-pattern mute into UI, keep engine mute clear.
   */
  function adoptMuteSolo(
    nextClip: ClipIR,
    legacyMuted: boolean[] | undefined,
    soloIn: boolean[],
    activePatternId?: string,
    trackCount?: number,
  ): boolean[] {
    migrateLegacyMuteToPatterns(nextClip, legacyMuted);
    const n = trackCount
      ?? Math.max(
        1,
        ...nextClip.patterns.map(p => p.tracks.length),
        soloIn.length,
      );
    const solo = normalizeMuteSoloFlags(soloIn, n);
    const muted = patternMuteFlags(
      nextClip,
      activePatternId ?? nextClip.patterns[0]?.id,
      n,
    );
    tracker.setMuteSolo(muted, solo, { emit: false });
    patchUi({ trackMuted: muted, trackSolo: solo });
    patchProject({ muted, solo });
    syncSessionMuteSolo(solo, n);
    return muted;
  }

  function setClip(next: ClipIR, options?: {
    syncDsl?: boolean;
    syncGrid?: boolean;
    resetHistory?: boolean;
    persist?: boolean;
    activePatternId?: string;
  }): void {
    syncLinearArrangement(next);
    clip = next;
    if (!clip.scenes.some(scene => scene.id === activeSceneId)) {
      activeSceneId = clip.scenes[0]?.id ?? "S0";
    }
    syncLoopButton();
    syncSynthSlotLabels(clip);
    syncing = true;
    try {
      if (options?.syncDsl !== false) {
        patchUi({ dslDraft: stringifyPattern(clip) });
      }
      if (options?.syncGrid !== false) {
        tracker.setClip(clip, {
          resetHistory: options?.resetHistory,
          activePatternId: options?.activePatternId ?? tracker.getActivePatternId(),
        });
      }
    } finally {
      syncing = false;
    }
    syncStepsField();
    syncLiveClip(clip);
    patchProject({ clip });
    patchTransport({
      bpm: clip.bpm,
      stepDivision: clip.stepDivision,
      loop: clip.loop,
      patternSteps: activePatternSteps(),
    });
    patchUi({ activePatternId: tracker.getActivePatternId(), activeSceneId });
    if (options?.persist !== false) {
      persist.schedule({
        clip,
        panels: {
          activePatternId: tracker.getActivePatternId(),
          activeSceneId,
        },
      });
    }
    updateTimeDisplay();
  }

  function syncLoopButton(): void {
    patchTransport({ loop: clip.loop });
  }

  function syncSynthSlotLabels(_next: ClipIR): void {
    // Track names for dock tabs: InstrumentDock uses factory labels for now.
  }

  function setClipLoop(loop: boolean): void {
    const from = tracker.getClip().loop === true;
    if (from === loop) {
      syncLoopButton();
      return;
    }
    getAdamCommandHost().execute(new SetClipLoopCommand(from, loop));
  }

  function applyClipLoopLive(loop: boolean): void {
    clip = tracker.getClip();
    if (clip.loop === loop) {
      syncLoopButton();
      return;
    }
    clip.loop = loop;
    setClip(clip, { resetHistory: false });
    setStatus(loop ? "Loop on" : "Loop off");
  }

  function applyPatternLength(nextSteps: number): void {
    if (session?.isMusicPlaying()) {
      stopPlayback();
    }
    clip = tracker.getClip();
    const pattern = findPattern(clip, tracker.getActivePatternId());
    if (!pattern) {
      return;
    }
    const before = patternSteps(pattern);
    const target = Math.max(1, Math.round(nextSteps));
    if (target === before) {
      setStatus(`Pattern ${pattern.id} length ${before}.`);
      tracker.focus();
      return;
    }
    try {
      getAdamCommandHost().execute(
        new ResizePatternCommand(pattern.id, structuredClone(pattern), target),
      );
    } catch {
      return;
    }
    const after = findPattern(tracker.getClip(), pattern.id);
    const afterSteps = after ? patternSteps(after) : target;
    setStatus(
      afterSteps > before
        ? `Pattern ${pattern.id} length ${before} → ${afterSteps} (added empty steps).`
        : afterSteps < before
        ? `Pattern ${pattern.id} length ${before} → ${afterSteps} (trimmed from the end).`
        : `Pattern ${pattern.id} length ${afterSteps}.`,
    );
    tracker.focus();
  }

  function selectActivePattern(patternId: string, options?: { focus?: boolean }): void {
    clip = tracker.getClip();
    const index = clip.patterns.findIndex(p => p.id === patternId);
    if (index < 0) {
      return;
    }
    const prevLoop = uiStore.getValue().patternLoop;
    // Keep a multi-pattern loop when clicking a pattern already inside it;
    // only snap the loop brace when selecting outside the current range.
    const nextLoop = patternLoopContainsIndex(prevLoop, index, clip.patterns.length)
      ? clampPatternLoop(prevLoop, clip.patterns.length)
      : patternLoopAtIndex(index, clip.patterns.length);
    const samePattern = tracker.getActivePatternId() === patternId;
    const sameLoop = prevLoop.start === nextLoop.start && prevLoop.length === nextLoop.length;
    if (samePattern && sameLoop) {
      if (options?.focus !== false) {
        tracker.focus();
      }
      return;
    }
    // Keep playing when switching edit focus inside the current loop brace;
    // only stop when the brace has to move (selection outside the brace).
    if (!sameLoop && session?.isMusicPlaying()) {
      stopPlayback();
    }
    applyPatternLoop(nextLoop, { syncTracker: true });
    tracker.setActivePatternId(patternId);
    clip = tracker.getClip();
    syncStepsField();
    patchUi({ activePatternId: patternId });
    persist.schedule({ panels: { activePatternId: patternId, patternLoop: nextLoop } });
    setStatus(`Active pattern ${patternId}`);
    updateTimeDisplay();
    syncArrangeCursorFromTracker();
    if (options?.focus !== false) {
      tracker.focus();
    }
  }

  function setPatternLoop(range: PatternLoopRange): void {
    clip = tracker.getClip();
    const from = clampPatternLoop(uiStore.getValue().patternLoop, clip.patterns.length);
    const to = clampPatternLoop(range, clip.patterns.length);
    if (loopEqual(from, to)) {
      applyPatternLoop(to);
      return;
    }
    getAdamCommandHost().execute(new SetPatternLoopCommand(from, to));
  }

  function createPattern(): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const id = nextPatternId(clip);
    const steps = activePatternSteps() || clip.stepDivision || 16;
    const pattern = createBlankPattern(clip, id, steps);
    const afterId = tracker.getActivePatternId();
    try {
      getAdamCommandHost().execute(
        new CreatePatternCommand(pattern, afterId, { label: "Create pattern" }),
      );
    } catch {
      return;
    }
    setStatus(`Created ${id} (${steps} rows)`);
  }

  function renamePattern(patternId: string, name: string): void {
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    if (!pattern) {
      return;
    }
    const from = pattern.name;
    const to = name ? name.slice(0, 48) : undefined;
    if ((from ?? undefined) === to) {
      return;
    }
    getAdamCommandHost().execute(new RenamePatternCommand(patternId, from, to));
    setStatus(to ? `Renamed ${patternId} → ${to}` : `Cleared name on ${patternId}`);
  }

  function deletePattern(patternId: string): void {
    try {
      getAdamCommandHost().execute(new DeletePatternCommand(patternId));
    } catch {
      return;
    }
    setStatus(`Deleted ${patternId}`);
  }

  function duplicatePattern(patternId: string): void {
    clip = tracker.getClip();
    const source = findPattern(clip, patternId);
    if (!source) {
      return;
    }
    const copy = clonePatternAsNew(clip, source);
    try {
      getAdamCommandHost().execute(
        new CreatePatternCommand(copy, patternId, { label: "Duplicate pattern" }),
      );
    } catch {
      return;
    }
    setStatus(`Duplicated ${patternId} → ${copy.id}`);
  }

  function reorderPatterns(fromIndex: number, toIndex: number): void {
    try {
      getAdamCommandHost().execute(new ReorderPatternsCommand(fromIndex, toIndex));
    } catch {
      return;
    }
    setStatus("Arrangement reordered");
  }

  function copyPattern(patternId: string): void {
    clip = tracker.getClip();
    const source = findPattern(clip, patternId);
    if (!source) {
      return;
    }
    setArrangeClipboard({ kind: "pattern", pattern: structuredClone(source) });
    setStatus(`Copied ${patternId}`);
  }

  function cutPattern(patternId: string): void {
    clip = tracker.getClip();
    if (clip.patterns.length <= 1) {
      setStatus("Keep at least one pattern.");
      return;
    }
    const source = findPattern(clip, patternId);
    if (!source) {
      return;
    }
    setArrangeClipboard({ kind: "pattern", pattern: structuredClone(source) });
    deletePattern(patternId);
    setStatus(`Cut ${patternId}`);
  }

  function pastePattern(afterPatternId?: string): void {
    const board = getArrangeClipboard();
    if (!board || board.kind !== "pattern") {
      setStatus("Clipboard has no pattern.");
      return;
    }
    clip = tracker.getClip();
    const afterId = afterPatternId ?? tracker.getActivePatternId();
    const copy = clonePatternAsNew(clip, board.pattern);
    try {
      getAdamCommandHost().execute(
        new CreatePatternCommand(copy, afterId, { label: "Paste pattern" }),
      );
    } catch {
      return;
    }
    setStatus(`Pasted ${copy.id}`);
  }

  function copyTrack(patternId: string, track: number): void {
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    const cells = pattern?.tracks[track]?.cells;
    if (!cells) {
      return;
    }
    setArrangeClipboard({ kind: "track", cells: cloneTrackCells(cells) });
    setStatus(`Copied track ${track + 1} from ${patternId}`);
  }

  function pasteTrack(patternId: string, track: number): void {
    const board = getArrangeClipboard();
    if (!board || board.kind !== "track") {
      setStatus("Clipboard has no track.");
      return;
    }
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    const dest = pattern?.tracks[track];
    if (!pattern || !dest) {
      return;
    }
    const from = cloneTrackCells(dest.cells);
    const to = fitCellsToSteps(board.cells, patternSteps(pattern));
    getAdamCommandHost().execute(new PasteTrackCellsCommand(patternId, track, from, to));
    setStatus(`Pasted track into ${patternId} · T${track + 1}`);
  }

  function openPatternTrack(
    patternId: string,
    track: number,
    options?: { focus?: boolean },
  ): void {
    const shouldFocus = options?.focus !== false;
    const keepStep = tracker.getActivePatternId() === patternId;
    const step = keepStep ? tracker.getCursorStep() : 0;
    selectActivePattern(patternId, { focus: false });
    tracker.jumpToCell(step, track as InstrumentSlot, 0, { focus: shouldFocus });
  }

  function selectActiveScene(sceneId: string): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    if (!clip.scenes?.some(scene => scene.id === sceneId)) {
      return;
    }
    if (activeSceneId === sceneId) {
      return;
    }
    activeSceneId = sceneId;
    patchUi({ activeSceneId: sceneId });
    persist.schedule({ panels: { activeSceneId: sceneId } });
    setStatus(`Active scene ${sceneId}`);
    updateTimeDisplay();
  }

  function createScene(): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const id = nextSceneId(clip);
    const patternId = tracker.getActivePatternId();
    clip.scenes!.push({
      id,
      order: clip.patterns.some(p => p.id === patternId) ? [patternId] : [clip.patterns[0]!.id],
    });
    if (!clip.order.includes(id)) {
      clip.order.push(id);
    }
    activeSceneId = id;
    setClip(clip, { resetHistory: false });
    persist.schedule({ panels: { activeSceneId: id } });
    setStatus(`Created scene ${id}`);
  }

  function renameScene(sceneId: string, name: string): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const scene = findScene(clip, sceneId);
    if (!scene) {
      return;
    }
    if (name) {
      scene.name = name.slice(0, 48);
    } else {
      delete scene.name;
    }
    setClip(clip, { resetHistory: false });
    setStatus(name ? `Renamed ${sceneId} → ${name}` : `Cleared name on ${sceneId}`);
  }

  function deleteScene(sceneId: string): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    if ((clip.scenes?.length ?? 0) <= 1) {
      setStatus("Keep at least one scene.");
      return;
    }
    clip.scenes = clip.scenes!.filter(scene => scene.id !== sceneId);
    clip.order = clip.order.filter(id => id !== sceneId);
    if (clip.order.length === 0) {
      clip.order = [clip.scenes[0]!.id];
    }
    if (activeSceneId === sceneId) {
      activeSceneId = clip.scenes[0]!.id;
    }
    setClip(clip, { resetHistory: false });
    persist.schedule({ panels: { activeSceneId } });
    setStatus(`Deleted scene ${sceneId}`);
  }

  function setScenePatternOrder(sceneId: string, patternIds: string[]): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    setSceneOrder(clip, sceneId, patternIds);
    setClip(clip, { resetHistory: false });
    const scene = findScene(clip, sceneId);
    setStatus(`${sceneId}: ${scene?.order.join(" → ") || "(empty)"}`);
  }

  function startScenePlayback(): void {
    if (!session) {
      return;
    }
    if (performancePlaying) {
      stopPerformance({ silent: true });
    }
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const scene = findScene(clip, activeSceneId);
    if (!scene || expandSceneOrder(clip, scene.id).length === 0) {
      setStatus("Scene has no patterns to play.");
      return;
    }
    patchUi({ dslDraft: stringifyPattern(clip), dslWarnings: "" });
    playingScope = "scene";
    syncLiveClip(clip, "scene");
    persist.schedule({ clip });
    session.playMusic(LIVE, {
      loop: clip.loop,
      startStep: 0,
    });
    const steps = clipOrderSteps(scopedPlayClip(clip, "scene"));
    setStatus(
      `Playing scene ${scene.id} · ${clip.bpm} BPM · ${steps} steps · loop ${clip.loop ? "on" : "off"}`,
    );
    updateTimeDisplay();
  }

  function applyDslFromTextarea(): boolean {
    const { clip: next, warnings } = parsePatternString(uiStore.getValue().dslDraft);
    patchUi({ dslWarnings: warnings.join("\n") });
    if (warnings.length > 0 && next.patterns.length === 0) {
      setStatus("DSL parse failed — see warnings.");
      return false;
    }
    // Full clip replace — clear undo (no invertible DSL diff yet).
    setClip(next, { syncDsl: false, syncGrid: true, resetHistory: true });
    clearAdamSessionHistory();
    setStatus(warnings.length ? `DSL applied with ${warnings.length} warning(s).` : "DSL applied → grid.");
    return true;
  }

  async function enableAudio(): Promise<void> {
    try {
      if (performanceBus) {
        performanceBus.stop();
        performanceBus = null;
        patchPerformance({ recording: false });
      }
      performancePlaying = false;
      patchPerformance({ playing: false, playheadRow: null });
      session?.dispose();
      engine?.dispose();
      engine = await createAudioEngine({
        mode: "worklet",
        workletUrl: labWorkletUrl(),
      });
      await engine.resume();
      session = new AudioSession(engine, {
        // Keep short so live grid/mute edits only need a small pending cancel/rebuild.
        lookahead: 0.25,
        intervalMs: 25,
        // Music PatternPlayer runs inside the AudioWorklet (AudioContext clock).
        scheduler: "worklet",
        onKernelMessage: handleMusicKernelMessage,
        onPlayhead(info) {
          lastPlayhead = info;
          if (performancePlaying) {
            if (!info) {
              if (!session?.isMusicPlaying()) {
                stopPerformance({ silent: true });
              }
              updateTimeDisplay();
              return;
            }
            onPerformancePlayhead(info.row);
            updateTimeDisplay();
            return;
          }
          if (!info) {
            // Natural end (loop off) — drop play scope and restore full clip.
            if (playingScope && !session?.isMusicPlaying()) {
              playingScope = null;
              syncLiveClip(clip, null);
            }
            tracker.setPlayheadRow(undefined);
            syncArrangePlayhead(null);
            patchTransport({
              playhead: null,
              playing: session?.isMusicPlaying() ?? false,
              playScope: playingScope,
            });
          } else {
            // Map pattern-local playhead → stacked view row. Keep the previous
            // highlight if the pattern isn't in the current view (don't flash off).
            const global = tracker.locateGlobalStep(info.patternId, info.row);
            if (global !== null) {
              tracker.setPlayheadRow(global);
            }
            // Arrange line + gold row are imperative (no transportStore / React).
            syncArrangePlayhead({ patternId: info.patternId, row: info.row });
          }
          updateTimeDisplay();
        },
      });
      syncSessionMuteSolo(tracker.getSolo());
      await pushAllInstrumentsAsync(projectStore.getValue().instruments);
      engine.transport.setSlotMixes(projectStore.getValue().slotMix);
      engine.transport.setSlotFxStacks(projectStore.getValue().slotFx);
      pushLiveFxStack(projectStore.getValue().fx);
      setArmed(true);
      patchTransport({ audioStarted: true });
      setClip(clip, { persist: false });
      const restoreSlot = saved.panels?.instrumentSlot ?? saved.panels?.synthSlot;
      if (restoreSlot != null) {
        openInstrument(restoreSlot);
      }
      setStatus(`Worklet ready @ ${engine.context?.sampleRate ?? "?"} Hz`);
      tracker.focus();
    } catch (error) {
      setStatus(`Failed to start engine: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  function startPlayback(): void {
    if (!session) {
      return;
    }
    if (performancePlaying) {
      stopPerformance({ silent: true });
    }
    clip = tracker.getClip();
    patchUi({ dslDraft: stringifyPattern(clip), dslWarnings: "" });
    playingScope = "range";
    // Pass scope explicitly — music is not playing yet, so we must not rely on
    // a post-hoc clear of playingScope that would register the full project.
    syncLiveClip(clip, "range");
    persist.schedule({ clip });
    const loop = currentPatternLoop();
    const ids = patternIdsInLoop(clip.patterns, loop);
    const startStep = tracker.getCursorStep();
    const startRef = tracker.locateViewStep(startStep);
    const startPatternId = startRef?.patternId ?? ids[0] ?? tracker.getActivePatternId();
    // startStep is in the scoped clip's order (= stacked view steps for range play).
    session.playMusic(LIVE, {
      loop: clip.loop,
      startStep,
    });
    syncArrangePlayhead({
      patternId: startPatternId,
      row: startRef?.localStep ?? 0,
    });
    if (startRef) {
      const global = tracker.locateGlobalStep(startPatternId, startRef.localStep);
      if (global !== null) {
        tracker.setPlayheadRow(global);
      }
    }
    patchTransport({
      playing: true,
      playScope: "range",
      bpm: clip.bpm,
      loop: clip.loop,
    });
    const label = ids.length === 1
      ? ids[0]
      : `${ids[0]}…${ids[ids.length - 1]}`;
    setStatus(
      `Playing ${label} from row ${startStep} · ${clip.bpm} BPM · ${patternLoopSteps(clip.patterns, loop)} steps · loop ${clip.loop ? "on" : "off"}`,
    );
    updateTimeDisplay();
    tracker.focus();
  }

  function stopPlayback(): void {
    if (performancePlaying) {
      stopPerformance();
      return;
    }
    session?.stopMusic();
    externalMidiSink.allNotesOff();
    playingScope = null;
    lastPlayhead = undefined;
    tracker.setPlayheadRow(undefined);
    syncArrangePlayhead(null);
    // Restore full project clip in the session registry while idle.
    syncLiveClip(clip, null);
    patchTransport({ playing: false, playScope: null, playhead: null });
    setStatus("Stopped.");
    updateTimeDisplay();
  }

  function togglePlay(): void {
    if (!session) {
      setStatus("Enable audio first.");
      return;
    }
    if (session.isMusicPlaying()) {
      stopPlayback();
    } else {
      startPlayback();
    }
  }

  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    const el = target.closest("input, textarea, select, [contenteditable=''], [contenteditable='true']");
    if (!el) {
      return false;
    }
    if (el instanceof HTMLInputElement) {
      const type = el.type;
      // Allow Space play/stop on checkboxes/buttons/ranges — block on text-like fields.
      return !(
        type === "button"
        || type === "checkbox"
        || type === "radio"
        || type === "range"
        || type === "submit"
        || type === "reset"
        || type === "file"
        || type === "color"
      );
    }
    return true;
  }

  function onSpacePlayKey(event: KeyboardEvent): void {
    if (event.code !== "Space" && event.key !== " ") {
      return;
    }
    if (bouncing) {
      event.preventDefault();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    if (isTypingTarget(event.target)) {
      return;
    }
    event.preventDefault();
    togglePlay();
  }

  function onShiftEnterJumpKey(event: KeyboardEvent): void {
    if (event.key !== "Enter" || !event.shiftKey) {
      return;
    }
    if (bouncing) {
      event.preventDefault();
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }
    if (isTypingTarget(event.target)) {
      return;
    }
    event.preventDefault();
    tracker.jumpToFirstRow();
    if (session?.isMusicPlaying()) {
      startPlayback();
    }
  }

  window.addEventListener("keydown", onSpacePlayKey, true);
  window.addEventListener("keydown", onShiftEnterJumpKey, true);

  function openInstrument(slot: InstrumentSlot = lastInstrumentSlot): void {
    lastInstrumentSlot = slot;
    patchUi({ instrumentSlot: slot });
    persist.schedule({ panels: { instrumentSlot: slot, synthSlot: slot } });
    const name = projectStore.getValue().presets[slot]?.name ?? `slot ${slot + 1}`;
    setStatus(`Instrument · ${name}`);
  }

  function closeInstrument(): void {
    if (uiStore.getValue().instrumentSlot == null) {
      return;
    }
    lastInstrumentSlot = uiStore.getValue().instrumentSlot ?? lastInstrumentSlot;
    patchUi({ instrumentSlot: null });
    persist.schedule({ panels: { instrumentSlot: null, synthSlot: null } });
    setStatus("Instrument closed.");
  }

  function toggleInstrument(): void {
    if (uiStore.getValue().instrumentSlot != null) {
      closeInstrument();
      return;
    }
    openInstrument(lastInstrumentSlot);
  }

  /** Author modal for tracker cursor track (if grid focused) or instruments panel slot. */
  function openInstrumentAuthor(): void {
    if (uiStore.getValue().authorOpen) {
      patchUi({ authorOpen: false });
      setStatus("Author closed.");
      return;
    }
    const active = document.activeElement;
    const trackerFocused =
      active instanceof Element && Boolean(active.closest(".tracker-host"));
    const slot = (
      trackerFocused
        ? tracker.getCursorTrack()
        : (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot)
    ) as InstrumentSlot;
    openInstrument(slot);
    const kind = projectStore.getValue().instruments[slot]?.kind ?? "synth";
    if (kind === "sf2") {
      setStatus("SoundFont instruments edit in the dock only.");
      return;
    }
    patchUi({ authorOpen: true });
    setStatus(
      kind === "drums"
        ? "Drum author open — drag the title bar to move."
        : kind === "wavetable"
        ? "Wavetable author open."
        : kind === "sampler"
        ? "Sampler author open — wave editor + preview."
        : "Synth author open — drag the title bar to move.",
    );
  }

  function pushLiveFxStack(effects: FxNode[]): void {
    const bypass = uiStore.getValue().fxBypass === true;
    if (session) {
      session.setFxStack(effects);
      session.setFxBypass(bypass);
    } else {
      engine?.transport.setFxStack(effects);
      engine?.transport.setFxBypass(bypass);
    }
  }

  function pushLiveSlotFxStacks(stacks: FxNode[][]): void {
    if (session) {
      session.setSlotFxStacks(stacks);
    } else {
      engine?.transport.setSlotFxStacks(stacks);
    }
  }

  function applyMasterFxLive(effects: FxNode[]): void {
    patchProject({ fx: effects });
    pushLiveFxStack(effects);
    persist.schedule({ fx: effects });
  }

  function applyFxStack(effects: FxNode[]): void {
    const from = structuredClone(projectStore.getValue().fx);
    const to = structuredClone(effects);
    if (fxEqual(from, to)) {
      return;
    }
    getAdamCommandHost().execute(new SetMasterFxCommand(from, to));
  }

  function applySlotFxLive(slot: InstrumentSlot, effects: FxNode[]): void {
    const next = patchSlotFxStack(projectStore.getValue().slotFx, slot, effects);
    patchProject({ slotFx: next });
    if (session) {
      session.setSlotFxStack(slot, next[slot] ?? []);
    } else {
      engine?.transport.setSlotFxStack(slot, next[slot] ?? []);
    }
    persist.schedule({ slotFx: next });
  }

  function applySlotFxStack(slot: InstrumentSlot, effects: FxNode[]): void {
    const from = structuredClone(projectStore.getValue().slotFx[slot] ?? []);
    const to = structuredClone(effects);
    if (fxEqual(from, to)) {
      return;
    }
    getAdamCommandHost().execute(new SetSlotFxCommand(slot, from, to));
  }

  function applySlotMixLive(slot: InstrumentSlot, mix: { volume: number; pan: number }): void {
    const next = patchSlotMix(projectStore.getValue().slotMix, slot, mix);
    patchProject({ slotMix: next });
    engine?.transport.setSlotMix(slot, next[slot]!);
    persist.schedule({ slotMix: next });
  }

  function setFxBypass(on: boolean): void {
    const bypass = on === true;
    patchUi({ fxBypass: bypass });
    // Keep the FX stack loaded; soft-ramp wet in the kernel so delay/reverb
    // memory stays warm and bypass doesn't click.
    if (session) {
      session.setFxBypass(bypass);
    } else {
      engine?.transport.setFxBypass(bypass);
    }
    setStatus(bypass ? "Master FX bypassed." : "Master FX active.");
  }

  function setFxScope(scope: "master" | "track"): void {
    patchUi({ fxScope: scope });
    if (scope === "track" && uiStore.getValue().instrumentSlot == null) {
      openInstrument(lastInstrumentSlot);
    }
    setStatus(scope === "master" ? "Editing master FX" : "Editing track FX");
  }

  function toggleFx(): void {
    if (uiStore.getValue().fxOpen) {
      patchUi({ fxOpen: false });
      persist.schedule({ panels: { fxOpen: false } });
      setStatus("FX closed.");
      return;
    }
    patchUi({ fxOpen: true, fxScope: "master" });
    persist.schedule({ panels: { fxOpen: true } });
    setStatus("Master FX open");
  }

  function togglePatterns(): void {
    if (uiStore.getValue().patternsOpen) {
      patchUi({ patternsOpen: false });
      persist.schedule({ panels: { patternsOpen: false } });
      setStatus("Patterns closed.");
      return;
    }
    patchUi({ patternsOpen: true });
    persist.schedule({ panels: { patternsOpen: true } });
    setStatus("Patterns open");
  }

  function toggleSamples(): void {
    if (uiStore.getValue().samplesOpen) {
      patchUi({ samplesOpen: false });
      persist.schedule({ panels: { samplesOpen: false } });
      setStatus("Samples closed.");
      return;
    }
    patchUi({ samplesOpen: true });
    persist.schedule({ panels: { samplesOpen: true } });
    setStatus("Samples open");
  }

  function toggleMixer(): void {
    const open = !uiStore.getValue().mixerOpen;
    patchUi({ mixerOpen: open });
    setStatus(open ? "Mixer open — drag the title bar to move." : "Mixer closed.");
  }

  function toggleSettings(): void {
    const open = !uiStore.getValue().settingsOpen;
    patchUi({ settingsOpen: open });
    setStatus(
      open
        ? "Settings — MIDI bridge & control bindings."
        : "Settings closed.",
    );
  }

  function togglePerformance(): void {
    const open = !uiStore.getValue().performanceOpen;
    patchUi({ performanceOpen: open });
    if (open) {
      resizePerformanceLanes(projectStore.getValue().instruments.length);
      setStatus("Performance open — launch patterns / tracks; use Play in the panel.");
    } else {
      setStatus("Performance closed.");
    }
  }

  function stopPerformance(options?: { silent?: boolean }): void {
    const wasPlaying = performancePlaying || performanceStore.getValue().playing;
    const wasRecording = performanceStore.getValue().recording || performanceBus != null;
    const previouslyActive = performanceStore
      .getValue()
      .lanes
      .map((lane, i) => (lane.activePatternId != null || lane.pending != null ? i : -1))
      .filter(i => i >= 0);
    performancePlaying = false;
    performanceLastRow = null;
    performanceLanePeriods = [];
    const lanes = performanceStore.getValue().lanes.map(() => ({
      activePatternId: null as string | null,
      pending: null,
    }));
    patchPerformance({
      playing: false,
      playheadRow: null,
      lanes,
    });
    // Drop Performance session mute overlay; arrange uses pattern mute again.
    syncSessionMuteSolo(uiStore.getValue().trackSolo);
    if (wasPlaying) {
      session?.stopMusic();
      lastPlayhead = undefined;
      tracker.setPlayheadRow(undefined);
      syncArrangePlayhead(null);
      syncLiveClip(clip, null);
      patchTransport({ playing: false, playScope: null, playhead: null });
      updateTimeDisplay();
      cutPerformanceSlots(previouslyActive);
    }
    if (wasRecording) {
      void finalizePerformanceRecording({
        silentStatus: options?.silent,
        stoppedTransport: wasPlaying,
      });
      return;
    }
    if (!options?.silent) {
      setStatus(wasPlaying ? "Performance stopped." : "Performance idle.");
    }
  }

  const PERFORMANCES_FOLDER_NAME = "performances";

  async function ensurePerformancesFolderId(): Promise<string> {
    await ensureSampleLibraryReady();
    const folders = await listSampleFolders();
    const existing = folders.find(
      folder => folder.name === PERFORMANCES_FOLDER_NAME && !folder.parentId,
    );
    if (existing) {
      return existing.id;
    }
    return createSampleFolderSync({ name: PERFORMANCES_FOLDER_NAME }).id;
  }

  async function finalizePerformanceRecording(options?: {
    silentStatus?: boolean;
    stoppedTransport?: boolean;
  }): Promise<void> {
    const recorder = performanceBus;
    performanceBus = null;
    patchPerformance({ recording: false });
    if (!recorder) {
      return;
    }
    const sampleRate = engine?.context?.sampleRate ?? 48_000;
    const pcm = recorder.stop();
    if (pcm.length < 256) {
      if (!options?.silentStatus) {
        setStatus(
          options?.stoppedTransport
            ? "Performance stopped · recording too short"
            : "Recording too short",
        );
      }
      return;
    }
    try {
      const folderId = await ensurePerformancesFolderId();
      const sampleName = `Performance ${new Date().toLocaleTimeString()}`;
      const meta = await ingestSamplePcm({
        name: sampleName,
        pcm,
        sampleRate,
        source: "record",
        folderId,
      });
      const seconds = (pcm.length / sampleRate).toFixed(1);
      if (!options?.silentStatus) {
        setStatus(
          options?.stoppedTransport
            ? `Performance stopped · saved “${meta.name}” (${seconds}s) → performances`
            : `Saved “${meta.name}” (${seconds}s) → performances`,
        );
      }
    } catch (error) {
      setStatus(
        `Performance record failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  async function togglePerformanceRecord(): Promise<void> {
    if (performanceBus || performanceStore.getValue().recording) {
      await finalizePerformanceRecording();
      return;
    }
    if (bouncing) {
      return;
    }
    try {
      if (!engine?.context || !transportStore.getValue().armed) {
        await enableAudio();
      }
      const ctx = engine?.context;
      const master = engine?.master;
      if (!ctx || !master) {
        setStatus("Audio engine not ready for Performance record");
        return;
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      performanceBus = startBusCapture(ctx, master);
      patchPerformance({ recording: true });
      if (!performancePlaying) {
        startPerformance();
      }
      if (!performancePlaying) {
        performanceBus.stop();
        performanceBus = null;
        patchPerformance({ recording: false });
        return;
      }
      setStatus(
        "Recording Performance… press Record again (or Stop) to save → performances",
      );
    } catch (error) {
      performanceBus?.stop();
      performanceBus = null;
      patchPerformance({ recording: false });
      setStatus(
        `Performance record failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  function startPerformance(): void {
    if (!session) {
      setStatus("Enable audio first.");
      return;
    }
    // Stop regular transport without wiping performance lane assignments.
    if (playingScope !== null || (session.isMusicPlaying() && !performancePlaying)) {
      session.stopMusic();
      playingScope = null;
      lastPlayhead = undefined;
      tracker.setPlayheadRow(undefined);
      syncArrangePlayhead(null);
      patchTransport({ playing: false, playScope: null, playhead: null });
    }
    clip = tracker.getClip();
    performancePlaying = true;
    performanceLastRow = null;
    const { quantumSteps } = pushPerformanceAssembly(clip);
    syncSessionMuteSolo(uiStore.getValue().trackSolo, undefined, tracker.getMuted());
    session.playMusic(LIVE, { loop: true, startStep: 0 });
    patchPerformance({ playing: true, playheadRow: 0, quantumSteps });
    setStatus(
      `Performance · ${clip.bpm} BPM · quantum ${quantumSteps} steps · launches wait for lane ends`,
    );
    updateTimeDisplay();
  }

  /** Queue (or cancel) stop for one Performance column at its pattern-end boundary. */
  function stopPerformanceLane(track: InstrumentSlot): void {
    const trackCount = Math.max(1, projectStore.getValue().instruments.length);
    resizePerformanceLanes(trackCount);
    const state = performanceStore.getValue();
    const lanes = state.lanes.map(lane => ({
      activePatternId: lane.activePatternId,
      pending: lane.pending ? { ...lane.pending } : null,
    }));
    const lane = lanes[track];
    if (!lane) {
      return;
    }
    if (!lane.activePatternId && !lane.pending) {
      setStatus(`I${track + 1} already idle`);
      return;
    }

    const playing = state.playing && performancePlaying;
    const anyActive = lanes.some(l => l.activePatternId != null);
    const applyImmediate = !playing || !anyActive;

    if (lane.pending?.kind === "stop") {
      lane.pending = null;
      patchPerformance({ lanes });
      setStatus(`Cancelled stop · I${track + 1}`);
      return;
    }

    // Pending launch only → cancel; otherwise queue/apply stop of active clip.
    if (!lane.activePatternId) {
      lane.pending = null;
      patchPerformance({ lanes });
      setStatus(`Cancelled launch · I${track + 1}`);
      return;
    }

    if (applyImmediate) {
      lane.activePatternId = null;
      lane.pending = null;
      patchPerformance({ lanes });
      if (playing) {
        pushPerformanceAssembly();
        cutPerformanceSlots([track]);
      }
      setStatus(`Stopped · I${track + 1}`);
      return;
    }

    lane.pending = { kind: "stop" };
    patchPerformance({ lanes });
    setStatus(`Stop queued · I${track + 1} (end of cycle)`);
  }

  function launchPerformanceCell(patternId: string, track: InstrumentSlot): void {
    const trackCount = Math.max(1, projectStore.getValue().instruments.length);
    resizePerformanceLanes(trackCount);
    const state = performanceStore.getValue();
    const lanes = state.lanes.map(lane => ({
      activePatternId: lane.activePatternId,
      pending: lane.pending ? { ...lane.pending } : null,
    }));
    const lane = lanes[track];
    if (!lane) {
      return;
    }

    const playing = state.playing && performancePlaying;
    // While anything is already looping, new launches wait for a pattern-end
    // boundary — idle lanes included (otherwise same-pattern peers start early).
    const anyActive = lanes.some(l => l.activePatternId != null);
    const applyImmediate = !playing || !anyActive;

    if (lane.activePatternId === patternId) {
      if (lane.pending?.kind === "stop") {
        lane.pending = null;
        patchPerformance({ lanes });
        setStatus(`Cancelled stop · I${track + 1}`);
        return;
      }
      if (lane.pending?.kind === "launch" && lane.pending.patternId === patternId) {
        lane.pending = null;
        patchPerformance({ lanes });
        return;
      }
      if (applyImmediate) {
        lane.activePatternId = null;
        lane.pending = null;
        patchPerformance({ lanes });
        if (playing) {
          pushPerformanceAssembly();
          cutPerformanceSlots([track]);
        }
        setStatus(`Stopped · I${track + 1}`);
        return;
      }
      lane.pending = { kind: "stop" };
      patchPerformance({ lanes });
      setStatus(`Stop queued · I${track + 1}`);
      return;
    }

    if (lane.pending?.kind === "launch" && lane.pending.patternId === patternId) {
      lane.pending = null;
      patchPerformance({ lanes });
      setStatus(`Cancelled launch · ${patternId} I${track + 1}`);
      return;
    }

    if (applyImmediate) {
      lane.activePatternId = patternId;
      lane.pending = null;
      patchPerformance({ lanes });
      if (playing) {
        pushPerformanceAssembly();
      }
      setStatus(`Playing · ${patternId} I${track + 1}`);
      return;
    }

    lane.pending = { kind: "launch", patternId };
    patchPerformance({ lanes });
    setStatus(`Launch queued · ${patternId} I${track + 1}`);
  }

  function launchPerformancePattern(patternId: string): void {
    const trackCount = Math.max(1, projectStore.getValue().instruments.length);
    resizePerformanceLanes(trackCount);
    const source = tracker.getClip();
    const pattern = findPattern(source, patternId);
    const contentTracks = Array.from({ length: trackCount }, (_, t) => t).filter(
      t => trackHasContent(pattern?.tracks[t]?.cells),
    );
    if (contentTracks.length === 0) {
      setStatus(`Pattern ${patternId} has no notes to launch`);
      return;
    }

    const state = performanceStore.getValue();
    const lanes = state.lanes.map(lane => ({
      activePatternId: lane.activePatternId,
      pending: lane.pending ? { ...lane.pending } : null,
    }));
    const allActive = contentTracks.every(t => lanes[t]?.activePatternId === patternId);
    const anyPending = contentTracks.some(t => lanes[t]?.pending != null);
    const playing = state.playing && performancePlaying;
    const anyActive = lanes.some(lane => lane.activePatternId != null);
    const applyImmediate = !playing || !anyActive;

    if (allActive && !anyPending) {
      const stoppedSlots: number[] = [];
      for (const t of contentTracks) {
        const lane = lanes[t]!;
        if (applyImmediate) {
          if (lane.activePatternId != null) {
            stoppedSlots.push(t);
          }
          lane.activePatternId = null;
          lane.pending = null;
        } else {
          lane.pending = { kind: "stop" };
        }
      }
      patchPerformance({ lanes });
      if (playing && contentTracks.every(t => lanes[t]!.activePatternId === null && !lanes[t]!.pending)) {
        pushPerformanceAssembly();
        cutPerformanceSlots(stoppedSlots);
      }
      setStatus(
        playing && contentTracks.some(t => lanes[t]!.pending?.kind === "stop")
          ? `Stop queued · pattern ${patternId}`
          : `Stopped · pattern ${patternId}`,
      );
      return;
    }

    for (const t of contentTracks) {
      const lane = lanes[t]!;
      if (lane.activePatternId === patternId && !lane.pending) {
        continue;
      }
      if (applyImmediate) {
        lane.activePatternId = patternId;
        lane.pending = null;
      } else {
        lane.pending = { kind: "launch", patternId };
      }
    }
    patchPerformance({ lanes });
    if (playing && contentTracks.every(t => lanes[t]!.pending === null)) {
      pushPerformanceAssembly();
    }
    const queued = contentTracks.some(t => lanes[t]!.pending?.kind === "launch");
    setStatus(
      queued
        ? `Launch queued · pattern ${patternId}`
        : `Playing · pattern ${patternId}`,
    );
  }

  /** Rapid Shift+Shift opens go-to search (JetBrains-style). */
  let lastLoneShiftAt = 0;
  function onDoubleShiftGoToKey(event: KeyboardEvent): void {
    if (uiStore.getValue().goToOpen) {
      lastLoneShiftAt = 0;
      return;
    }
    if (event.key !== "Shift" || event.repeat) {
      if (event.key !== "Shift") {
        lastLoneShiftAt = 0;
      }
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) {
      lastLoneShiftAt = 0;
      return;
    }
    const now = performance.now();
    if (now - lastLoneShiftAt < 400) {
      lastLoneShiftAt = 0;
      event.preventDefault();
      patchUi({ goToOpen: true });
      setStatus("Go to — type a row number or marker name.");
      return;
    }
    lastLoneShiftAt = now;
  }
  window.addEventListener("keydown", onDoubleShiftGoToKey, true);

  function setBpmFromUi(raw: number): void {
    clip = tracker.getClip();
    const from = clip.bpm;
    const to = raw || from;
    if (to === from) {
      return;
    }
    getAdamCommandHost().execute(new SetBpmCommand(from, to));
  }

  function setGridFromUi(raw: number): void {
    clip = tracker.getClip();
    const from = clip.stepDivision;
    const to = Number.isFinite(raw)
      ? Math.min(64, Math.max(1, Math.round(raw)))
      : from;
    if (to === from) {
      patchTransport({ stepDivision: from });
      return;
    }
    getAdamCommandHost().execute(new SetStepDivisionCommand(from, to));
    setStatus(`Grid ${to} · ${((60 / clip.bpm) * (4 / to)).toFixed(3)}s/step`);
  }

  function loadDemo(id: LabDemoId): void {
    const demo = LAB_DEMOS.find(entry => entry.id === id);
    if (!demo) {
      return;
    }
    const loaded = demo.load();
    if (session?.isMusicPlaying()) {
      stopPlayback();
    }
    const nextClip = loaded.clip;
    const instruments = loaded.instruments ?? instrumentsFromPresets(loaded.presets);
    const trackCount = instruments.length;
    const presets = presetsFromInstruments(instruments);
    const solo = normalizeMuteSoloFlags(loaded.solo, trackCount);
    const muted = adoptMuteSolo(nextClip, loaded.muted, solo, nextClip.patterns[0]?.id, trackCount);
    const slotMix = loaded.slotMix ?? createDefaultSlotMixes(trackCount);
    const slotFx = loaded.slotFx ?? createDefaultSlotFxStacks(trackCount);
    const bank = loaded.bank ?? projectStore.getValue().bank;
    const drumBank = loaded.drumBank ?? projectStore.getValue().drumBank;
    const fx = loaded.fx;

    setClip(nextClip, { resetHistory: true });
    activeSceneId = nextClip.scenes[0]?.id ?? "S0";
    slotBankIds = syncSlotBankIdsFromPresets({
      presets,
      bank,
      slotBankIds: initialSlotBankIds(),
    }).slotBankIds;
    void pushAllInstrumentsAsync(instruments);
    const trackColors = normalizeTrackColors(null, trackCount);
    patchProject({
      instruments,
      presets,
      bank,
      drumBank,
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
      fx,
    });
    tracker.setTrackColors(trackColors);
    pushLiveFxStack(fx);
    engine?.transport.setSlotMixes(slotMix);
    pushLiveSlotFxStacks(slotFx);
    applyPatternLoop({ start: 0, length: 1 });
    setControlBindings([]);
    persist.schedule({
      clip: nextClip,
      instruments,
      presets,
      bank,
      drumBank,
      trackColors,
      slotMix,
      slotFx,
      muted,
      solo,
      fx,
      panels: { activeSceneId, patternLoop: { start: 0, length: 1 } },
    });
    patchUi({ dslWarnings: "" });
    setStatus(`Loaded demo · ${demo.description}`);
    tracker.jumpToCell(0, 0, 0);
  }

  function setMaterialMode(mode: MaterialMode, opts: { persist?: boolean } = {}): void {
    applyMaterial(mode, document.documentElement);
    patchUi({ material: mode });
    if (opts.persist !== false) {
      persist.schedule({ material: mode });
    }
  }

  function newProject(): void {
    if (!confirm("Reset to an empty 8-track pattern?\n\nBank, FX stack, and panel settings are kept. Instruments reset to factory defaults.")) {
      return;
    }
    if (session?.isMusicPlaying()) {
      stopPlayback();
    }
    const next = createEmptyClip({
      bpm: 120,
      steps: 16,
      stepDivision: 16,
      trackCount: DEFAULT_INSTRUMENT_SLOT_COUNT,
    });
    const instruments = createDefaultInstruments(DEFAULT_INSTRUMENT_SLOT_COUNT);
    const muted = createDefaultMuteSolo(DEFAULT_INSTRUMENT_SLOT_COUNT);
    const solo = createDefaultMuteSolo(DEFAULT_INSTRUMENT_SLOT_COUNT);
    const slotMix = createDefaultSlotMixes(DEFAULT_INSTRUMENT_SLOT_COUNT);
    const slotFx = createDefaultSlotFxStacks(DEFAULT_INSTRUMENT_SLOT_COUNT);
    activeSceneId = "S0";
    const trackColors = normalizeTrackColors(null, DEFAULT_INSTRUMENT_SLOT_COUNT);
    patchProject({
      instruments,
      presets: presetsFromInstruments(instruments),
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
    });
    void pushAllInstrumentsAsync(instruments);
    tracker.setMuteSolo(muted, solo, { emit: false });
    tracker.setTrackColors(trackColors);
    patchUi({ trackMuted: muted, trackSolo: solo });
    syncSessionMuteSolo(solo);
    for (let slot = 0; slot < slotMix.length; slot++) {
      engine?.transport.setSlotMix(slot as InstrumentSlot, slotMix[slot]!);
    }
    pushLiveSlotFxStacks(slotFx);
    setClip(next, { resetHistory: true, activePatternId: "P0" });
    applyPatternLoop({ start: 0, length: 1 });
    setControlBindings([]);
    patchUi({ dslWarnings: "" });
    persist.schedule({
      clip: next,
      instruments,
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
      panels: { activeSceneId: "S0", patternLoop: { start: 0, length: 1 } },
    });
    setStatus("New empty pattern · 8 tracks · presets & FX kept.");
    tracker.focus();
  }

  function addTrack(): void {
    try {
      getAdamCommandHost().execute(new AddTrackCommand());
    } catch {
      // Capacity / failure — status set in applyAddTrack.
    }
  }

  /** Append one empty instrument track (command bridge). */
  function applyAddTrack(): { slot: number } | null {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (n >= MAX_INSTRUMENT_SLOT_COUNT) {
      setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
      return null;
    }
    const slot = n as InstrumentSlot;
    const keepStep = tracker.getCursorStep();
    const clip = tracker.getClip();
    ensureClipTrackCount(clip, n);
    for (const pattern of clip.patterns) {
      const steps = Math.max(1, pattern.steps || 1);
      pattern.tracks.push({
        slot,
        name: defaultSlotName(slot),
        cells: Array.from({ length: steps }, () => emptyCell()),
      });
    }
    ensureClipTrackCount(clip, n + 1);

    const instrument = synthInstrument(defaultPresetForSlot(slot));
    const instruments = [...project.instruments.map(cloneInstrument), instrument];
    const presets = presetsFromInstruments(instruments);
    const slotMix = normalizeSlotMixes(project.slotMix, n + 1);
    const slotFx = normalizeSlotFxStacks(project.slotFx, n + 1);
    const solo = normalizeMuteSoloFlags(project.solo, n + 1);
    const muted = patternMuteFlags(clip, tracker.getActivePatternId(), n + 1);
    const trackColors = normalizeTrackColors(project.trackColors, n + 1);

    patchProject({ instruments, presets, slotMix, slotFx, muted, solo, trackColors });
    while (slotBankIds.length < n + 1) {
      slotBankIds.push(undefined);
    }
    while (wtSlotBankIds.length < n + 1) {
      wtSlotBankIds.push(undefined);
    }
    void pushInstrumentToEngine(slot, instrument);
    engine?.transport.setSlotMix(slot, slotMix[slot]!);
    engine?.transport.setSlotFxStack(slot, slotFx[slot] ?? []);
    tracker.setMuteSolo(muted, solo, { emit: false });
    tracker.setTrackColors(trackColors);
    patchUi({ trackMuted: muted, trackSolo: solo });
    syncSessionMuteSolo(solo, n + 1);
    resizePerformanceLanes(n + 1);
    setClip(clip, { resetHistory: false });
    persist.schedule({ clip, instruments, slotMix, slotFx, muted, solo, trackColors });
    setStatus(`Added track I${slot + 1} · ${n + 1} tracks`);
    tracker.jumpToCell(keepStep, slot, 0);
    return { slot };
  }

  function removeTrack(track: number): void {
    try {
      getAdamCommandHost().execute(new RemoveTrackCommand(track));
    } catch {
      // Last track / invalid — status set in applyRemoveTrack.
    }
  }

  function duplicateTrack(track: number): void {
    try {
      getAdamCommandHost().execute(new DuplicateTrackCommand(track));
    } catch {
      // Capacity / invalid — status set in applyDuplicateTrack.
    }
  }

  function copyInstrument(track: number): void {
    const payload = captureInstrumentSlot(track);
    if (!payload) {
      return;
    }
    setInstrumentClipboard(payload);
    setStatus(`Copied instrument I${track + 1}`);
  }

  function pasteInstrument(track: number): void {
    const board = getInstrumentClipboard();
    if (!board) {
      setStatus("Clipboard has no instrument.");
      return;
    }
    const from = captureInstrumentSlot(track);
    if (!from) {
      return;
    }
    try {
      getAdamCommandHost().execute(
        new PasteInstrumentCommand(track, from, cloneInstrumentSlotPayload(board)),
      );
      setStatus(`Pasted instrument → I${track + 1}`);
    } catch {
      // Invalid track — rare.
    }
  }

  function reorderTracks(fromIndex: number, toIndex: number): void {
    try {
      getAdamCommandHost().execute(new ReorderTracksCommand(fromIndex, toIndex));
    } catch {
      // Invalid indices — applyReorderTracks returns false.
    }
  }

  /** Move track `fromIndex` → `toIndex` (command bridge). */
  function applyReorderTracks(fromIndex: number, toIndex: number): boolean {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (
      !Number.isInteger(fromIndex)
      || !Number.isInteger(toIndex)
      || fromIndex < 0
      || toIndex < 0
      || fromIndex >= n
      || toIndex >= n
      || fromIndex === toIndex
    ) {
      return false;
    }

    const keepStep = tracker.getCursorStep();
    const keepTrack = tracker.getCursorTrack();
    const clip = tracker.getClip();

    for (const pattern of clip.patterns) {
      pattern.tracks = moveArrayIndex(pattern.tracks, fromIndex, toIndex);
      for (let i = 0; i < pattern.tracks.length; i++) {
        pattern.tracks[i]!.slot = i as InstrumentSlot;
      }
    }
    ensureClipTrackCount(clip, n);

    const instruments = moveArrayIndex(
      project.instruments.map(cloneInstrument),
      fromIndex,
      toIndex,
    );
    const presets = presetsFromInstruments(instruments);
    const slotMix = normalizeSlotMixes(
      moveArrayIndex(project.slotMix, fromIndex, toIndex),
      n,
    );
    const slotFx = normalizeSlotFxStacks(
      moveArrayIndex(project.slotFx, fromIndex, toIndex),
      n,
    );
    const solo = normalizeMuteSoloFlags(
      moveArrayIndex(project.solo, fromIndex, toIndex),
      n,
    );
    const trackColors = normalizeTrackColors(
      moveArrayIndex(project.trackColors, fromIndex, toIndex),
      n,
    );
    const muted = patternMuteFlags(clip, tracker.getActivePatternId(), n);

    slotBankIds = moveArrayIndex(slotBankIds, fromIndex, toIndex);
    wtSlotBankIds = moveArrayIndex(wtSlotBankIds, fromIndex, toIndex);
    samplerSlotBankIds = moveArrayIndex(samplerSlotBankIds, fromIndex, toIndex);

    const ui = uiStore.getValue();
    let nextInstrumentSlot = ui.instrumentSlot;
    if (nextInstrumentSlot != null) {
      nextInstrumentSlot = mapTrackIndexAfterReorder(
        nextInstrumentSlot,
        fromIndex,
        toIndex,
      ) as InstrumentSlot;
    }

    const bindings = controlSessionStore.getValue().bindings.map(entry => ({
      ...entry,
      target: remapTrackControlPath(entry.target, fromIndex, toIndex),
    }));
    setControlBindings(bindings);

    patchProject({ instruments, presets, slotMix, slotFx, muted, solo, trackColors });
    patchUi({
      instrumentSlot: nextInstrumentSlot,
      trackMuted: muted,
      trackSolo: solo,
      trackLabelEdit: null,
    });
    tracker.setMuteSolo(muted, solo, { emit: false });
    tracker.setTrackColors(trackColors);
    syncSessionMuteSolo(solo, n);
    reorderPerformanceLanes(fromIndex, toIndex);
    setClip(clip, { resetHistory: false });
    void pushAllInstrumentsAsync(instruments);
    engine?.transport.setSlotMixes(slotMix);
    pushLiveSlotFxStacks(slotFx);
    persist.schedule({
      clip,
      instruments,
      presets,
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
    });

    const nextTrack = mapTrackIndexAfterReorder(keepTrack, fromIndex, toIndex) as InstrumentSlot;
    tracker.jumpToCell(keepStep, nextTrack, 0);
    setStatus(`Moved track I${fromIndex + 1} → I${toIndex + 1}`);
    return true;
  }

  function captureRemovedTrack(track: number): RemovedTrackPayload | null {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (n <= 1) {
      setStatus("Cannot delete the last track.");
      return null;
    }
    return captureTrackClone(track);
  }

  /** Snapshot a track for delete-restore or duplicate-insert (no capacity checks). */
  function captureTrackClone(track: number): RemovedTrackPayload | null {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (!Number.isInteger(track) || track < 0 || track >= n) {
      return null;
    }
    const clip = tracker.getClip();
    const patterns = clip.patterns.map(pattern => {
      const row = pattern.tracks[track];
      return {
        patternId: pattern.id,
        name: row?.name,
        muted: row?.muted === true,
        cells: cloneTrackCells(row?.cells ?? []),
      };
    });
    return {
      track,
      instrument: cloneInstrument(project.instruments[track]!),
      slotMix: structuredClone(project.slotMix[track] ?? { volume: 1, pan: 0 }),
      slotFx: structuredClone(project.slotFx[track] ?? []),
      solo: project.solo[track] === true,
      trackColor: normalizeTrackColors(project.trackColors, n)[track] ?? null,
      slotBankId: slotBankIds[track],
      wtSlotBankId: wtSlotBankIds[track],
      patterns,
      cursorStep: tracker.getCursorStep(),
    };
  }

  /** Instrument sound only — no pattern cells / tint / names. */
  function captureInstrumentSlot(track: number): InstrumentSlotPayload | null {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (!Number.isInteger(track) || track < 0 || track >= n) {
      return null;
    }
    while (samplerSlotBankIds.length < n) {
      samplerSlotBankIds.push(undefined);
    }
    return {
      instrument: cloneInstrument(project.instruments[track]!),
      slotMix: structuredClone(project.slotMix[track] ?? { volume: 1, pan: 0 }),
      slotFx: structuredClone(project.slotFx[track] ?? []),
      slotBankId: slotBankIds[track],
      wtSlotBankId: wtSlotBankIds[track],
      samplerSlotBankId: samplerSlotBankIds[track],
    };
  }

  function applyInstrumentSlot(track: number, payload: InstrumentSlotPayload): void {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (!Number.isInteger(track) || track < 0 || track >= n) {
      throw new Error("Invalid instrument slot");
    }
    const next = cloneInstrumentSlotPayload(payload);
    const instruments = project.instruments.map((inst, i) =>
      i === track ? next.instrument : cloneInstrument(inst),
    );
    const presets = presetsFromInstruments(instruments);
    const slotMix = normalizeSlotMixes(project.slotMix, n);
    slotMix[track] = normalizeSlotMix(next.slotMix);
    const slotFx = normalizeSlotFxStacks(project.slotFx, n);
    slotFx[track] = structuredClone(next.slotFx);

    slotBankIds = [...slotBankIds];
    while (slotBankIds.length < n) {
      slotBankIds.push(undefined);
    }
    slotBankIds[track] = next.slotBankId;

    wtSlotBankIds = [...wtSlotBankIds];
    while (wtSlotBankIds.length < n) {
      wtSlotBankIds.push(undefined);
    }
    wtSlotBankIds[track] = next.wtSlotBankId;

    samplerSlotBankIds = [...samplerSlotBankIds];
    while (samplerSlotBankIds.length < n) {
      samplerSlotBankIds.push(undefined);
    }
    samplerSlotBankIds[track] = next.samplerSlotBankId;

    patchProject({ instruments, presets, slotMix, slotFx });
    if (next.instrument.kind === "externalMidi") {
      void externalMidiSink.ensureAccess();
    }
    void pushAllInstrumentsAsync(instruments);
    engine?.transport.setSlotMixes(slotMix);
    pushLiveSlotFxStacks(slotFx);
    persist.schedule({ instruments, presets, slotMix, slotFx });
  }

  function withCopySuffix(name: string | undefined, fallback: string): string {
    const base = (name ?? "").trim() || fallback;
    if (/\scopy$/i.test(base)) {
      return base;
    }
    return `${base} copy`;
  }

  function instrumentWithCopyName(instrument: Instrument): Instrument {
    const next = cloneInstrument(instrument);
    if (next.kind === "drums") {
      next.kit = { ...next.kit, name: withCopySuffix(next.kit.name, "Drums") };
      return next;
    }
    if (next.kind === "sf2" || next.kind === "externalMidi") {
      return next;
    }
    next.preset = {
      ...next.preset,
      name: withCopySuffix(next.preset.name, defaultSlotName(0)),
    };
    return next;
  }

  /** Clone `source` and insert the copy immediately to its right. */
  function applyDuplicateTrack(source: number): { slot: number } | null {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    if (n >= MAX_INSTRUMENT_SLOT_COUNT) {
      setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
      return null;
    }
    const snapshot = captureTrackClone(source);
    if (!snapshot) {
      return null;
    }
    const insertAt = source + 1;
    const fallbackName = defaultSlotName(insertAt as InstrumentSlot);
    const payload: RemovedTrackPayload = {
      ...snapshot,
      track: insertAt,
      instrument: instrumentWithCopyName(snapshot.instrument),
      patterns: snapshot.patterns.map(entry => ({
        ...entry,
        name: withCopySuffix(entry.name, fallbackName),
      })),
      cursorStep: tracker.getCursorStep(),
    };
    applyRestoreRemovedTrack(payload);
    setStatus(`Duplicated track I${source + 1} → I${insertAt + 1} · ${n + 1} tracks`);
    return { slot: insertAt };
  }

  /** Delete one instrument track (command bridge). */
  function applyRemoveTrack(track: number): RemovedTrackPayload | null {
    const payload = captureRemovedTrack(track);
    if (!payload) {
      return null;
    }

    const project = projectStore.getValue();
    const n = project.instruments.length;
    const keepStep = tracker.getCursorStep();
    const keepTrack = tracker.getCursorTrack();
    const clip = tracker.getClip();

    for (const pattern of clip.patterns) {
      if (track < pattern.tracks.length) {
        pattern.tracks.splice(track, 1);
      }
      for (let i = 0; i < pattern.tracks.length; i++) {
        pattern.tracks[i]!.slot = i as InstrumentSlot;
      }
    }
    ensureClipTrackCount(clip, n - 1);

    const instruments = removeArrayIndex(project.instruments.map(cloneInstrument), track);
    const presets = presetsFromInstruments(instruments);
    const slotMix = normalizeSlotMixes(removeArrayIndex(project.slotMix, track), n - 1);
    const slotFx = normalizeSlotFxStacks(removeArrayIndex(project.slotFx, track), n - 1);
    const solo = normalizeMuteSoloFlags(removeArrayIndex(project.solo, track), n - 1);
    const muted = patternMuteFlags(clip, tracker.getActivePatternId(), n - 1);
    const trackColors = normalizeTrackColors(removeArrayIndex(project.trackColors, track), n - 1);

    slotBankIds = removeArrayIndex(slotBankIds, track);
    wtSlotBankIds = removeArrayIndex(wtSlotBankIds, track);
    samplerSlotBankIds = removeArrayIndex(samplerSlotBankIds, track);

    const ui = uiStore.getValue();
    let nextInstrumentSlot = ui.instrumentSlot;
    if (nextInstrumentSlot != null) {
      if (nextInstrumentSlot === track) {
        nextInstrumentSlot = Math.min(track, n - 2) as InstrumentSlot;
      } else if (nextInstrumentSlot > track) {
        nextInstrumentSlot = (nextInstrumentSlot - 1) as InstrumentSlot;
      }
    }

    patchProject({ instruments, presets, slotMix, slotFx, muted, solo, trackColors });
    patchUi({
      instrumentSlot: nextInstrumentSlot,
      trackMuted: muted,
      trackSolo: solo,
      trackLabelEdit: null,
    });
    tracker.setMuteSolo(muted, solo, { emit: false });
    tracker.setTrackColors(trackColors);
    syncSessionMuteSolo(solo, n - 1);
    resizePerformanceLanes(n - 1);
    setClip(clip, { resetHistory: false });
    void pushAllInstrumentsAsync(instruments);
    engine?.transport.setSlotMixes(slotMix);
    pushLiveSlotFxStacks(slotFx);
    persist.schedule({
      clip,
      instruments,
      presets,
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
    });
    setStatus(`Deleted track I${track + 1} · ${n - 1} tracks`);

    const nextTrack = (
      keepTrack > track ? keepTrack - 1 : Math.min(keepTrack, n - 2)
    ) as InstrumentSlot;
    tracker.jumpToCell(keepStep, nextTrack, 0);
    return payload;
  }

  function applyRestoreRemovedTrack(payload: RemovedTrackPayload): void {
    const project = projectStore.getValue();
    const n = project.instruments.length;
    const insertAt = Math.max(0, Math.min(payload.track, n));
    const clip = tracker.getClip();

    for (const pattern of clip.patterns) {
      const saved = payload.patterns.find(p => p.patternId === pattern.id);
      const steps = Math.max(1, pattern.steps || 1);
      const cells = saved
        ? fitCellsToSteps(saved.cells, steps)
        : Array.from({ length: steps }, () => emptyCell());
      const trackObj = {
        slot: insertAt as InstrumentSlot,
        name: saved?.name ?? defaultSlotName(insertAt as InstrumentSlot),
        cells,
        ...(saved?.muted ? { muted: true as const } : {}),
      };
      pattern.tracks.splice(insertAt, 0, trackObj);
      for (let i = 0; i < pattern.tracks.length; i++) {
        pattern.tracks[i]!.slot = i as InstrumentSlot;
      }
    }
    ensureClipTrackCount(clip, n + 1);

    const instruments = project.instruments.map(cloneInstrument);
    instruments.splice(insertAt, 0, cloneInstrument(payload.instrument));
    const presets = presetsFromInstruments(instruments);
    const slotMix = [...normalizeSlotMixes(project.slotMix, n)];
    slotMix.splice(insertAt, 0, structuredClone(payload.slotMix));
    const slotFx = [...normalizeSlotFxStacks(project.slotFx, n)];
    slotFx.splice(insertAt, 0, structuredClone(payload.slotFx));
    const solo = [...normalizeMuteSoloFlags(project.solo, n)];
    solo.splice(insertAt, 0, payload.solo);
    const trackColors = [...normalizeTrackColors(project.trackColors, n)];
    trackColors.splice(insertAt, 0, payload.trackColor);
    const muted = patternMuteFlags(clip, tracker.getActivePatternId(), n + 1);

    slotBankIds = [...slotBankIds];
    while (slotBankIds.length < n) {
      slotBankIds.push(undefined);
    }
    slotBankIds.splice(insertAt, 0, payload.slotBankId);
    wtSlotBankIds = [...wtSlotBankIds];
    while (wtSlotBankIds.length < n) {
      wtSlotBankIds.push(undefined);
    }
    wtSlotBankIds.splice(insertAt, 0, payload.wtSlotBankId);

    patchProject({
      instruments,
      presets,
      slotMix: normalizeSlotMixes(slotMix, n + 1),
      slotFx: normalizeSlotFxStacks(slotFx, n + 1),
      muted,
      solo: normalizeMuteSoloFlags(solo, n + 1),
      trackColors: normalizeTrackColors(trackColors, n + 1),
    });
    tracker.setMuteSolo(muted, normalizeMuteSoloFlags(solo, n + 1), { emit: false });
    tracker.setTrackColors(normalizeTrackColors(trackColors, n + 1));
    patchUi({
      trackMuted: muted,
      trackSolo: normalizeMuteSoloFlags(solo, n + 1),
    });
    syncSessionMuteSolo(solo, n + 1);
    setClip(clip, { resetHistory: false });
    void pushAllInstrumentsAsync(instruments);
    engine?.transport.setSlotMixes(normalizeSlotMixes(slotMix, n + 1));
    pushLiveSlotFxStacks(normalizeSlotFxStacks(slotFx, n + 1));
    persist.schedule({
      clip,
      instruments,
      presets,
      slotMix: normalizeSlotMixes(slotMix, n + 1),
      slotFx: normalizeSlotFxStacks(slotFx, n + 1),
      muted,
      solo: normalizeMuteSoloFlags(solo, n + 1),
      trackColors: normalizeTrackColors(trackColors, n + 1),
    });
    setStatus(`Restored track I${insertAt + 1} · ${n + 1} tracks`);
    tracker.jumpToCell(payload.cursorStep ?? tracker.getCursorStep(), insertAt as InstrumentSlot, 0);
  }

  function applySetPatternMute(patternId: string, track: number, muted: boolean): void {
    const clipNext = tracker.getClip();
    const trackObj = findPattern(clipNext, patternId)?.tracks[track];
    if (!trackObj) {
      return;
    }
    if (muted) {
      trackObj.muted = true;
    } else {
      delete trackObj.muted;
    }
    setClip(clipNext, { resetHistory: false });
    const n = projectStore.getValue().instruments.length;
    const mutedFlags = patternMuteFlags(clipNext, tracker.getActivePatternId(), n);
    const solo = normalizeMuteSoloFlags(projectStore.getValue().solo, n);
    tracker.setMuteSolo(mutedFlags, solo, { emit: true });
  }

  function applyGetPatternMute(patternId: string, track: number): boolean {
    return findPattern(tracker.getClip(), patternId)?.tracks[track]?.muted === true;
  }

  function applySetSolo(track: number, solo: boolean): void {
    const n = projectStore.getValue().instruments.length;
    if (!Number.isInteger(track) || track < 0 || track >= n) {
      return;
    }
    const nextSolo = normalizeMuteSoloFlags(projectStore.getValue().solo, n);
    nextSolo[track] = solo;
    const muted = normalizeMuteSoloFlags(tracker.getMuted(), n);
    tracker.setMuteSolo(muted, nextSolo, { emit: true });
  }

  function applyGetSolo(track: number): boolean {
    return tracker.getSolo()[track] === true;
  }

  function applyGetTrackColor(track: number): number | null {
    const n = Math.max(projectStore.getValue().instruments.length, track + 1);
    return normalizeTrackColors(projectStore.getValue().trackColors, n)[track] ?? null;
  }

  function applySetTrackColor(track: number, color: number | null): void {
    const n = Math.max(projectStore.getValue().instruments.length, track + 1);
    const next = [...normalizeTrackColors(projectStore.getValue().trackColors, n)];
    next[track] = color == null ? null : clampRowMarkerColor(color);
    patchProject({ trackColors: next });
    tracker.setTrackColors(next);
    persist.schedule({ trackColors: next, clip: tracker.getClip() });
    setStatus(
      next[track] == null
        ? `Track ${track + 1} tint cleared`
        : `Track ${track + 1} tint set`,
    );
  }

  registerAdamProjectBridge({
    addTrack: applyAddTrack,
    removeTrack: applyRemoveTrack,
    restoreRemovedTrack: applyRestoreRemovedTrack,
    duplicateTrack: applyDuplicateTrack,
    reorderTracks: applyReorderTracks,
    getInstrumentSlot: captureInstrumentSlot,
    setInstrumentSlot: applyInstrumentSlot,
    getPatternMute: applyGetPatternMute,
    setPatternMute: applySetPatternMute,
    getSolo: applyGetSolo,
    setSolo: applySetSolo,
    getTrackColor: applyGetTrackColor,
    setTrackColor: applySetTrackColor,
    getMasterFx: () => structuredClone(projectStore.getValue().fx),
    setMasterFx: effects => {
      applyMasterFxLive(structuredClone(effects));
    },
    getSlotFx: slot => structuredClone(projectStore.getValue().slotFx[slot] ?? []),
    setSlotFx: (slot, effects) => {
      applySlotFxLive(slot as InstrumentSlot, structuredClone(effects));
    },
    getSlotMix: slot => {
      const n = Math.max(projectStore.getValue().instruments.length, slot + 1);
      return normalizeSlotMix(normalizeSlotMixes(projectStore.getValue().slotMix, n)[slot]);
    },
    setSlotMix: (slot, mix) => {
      applySlotMixLive(slot as InstrumentSlot, normalizeSlotMix(mix));
    },
  });

  function arrangeInsertPatternAfter(
    pattern: import("../../../../src/lib/audio").Pattern,
    afterId: string | undefined,
    options?: {
      activePatternId?: string;
      snapLoopToNew?: boolean;
      patternLoop?: PatternLoopRange;
    },
  ): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const prevCount = clip.patterns.length;
    const prevLoop = clampPatternLoop(uiStore.getValue().patternLoop, prevCount);
    const afterIndex = afterId ? clip.patterns.findIndex(p => p.id === afterId) : -1;
    const insertAt = afterIndex < 0 ? prevCount : afterIndex + 1;
    insertPatternAfter(clip, structuredClone(pattern), afterId);
    const activeId = options?.activePatternId ?? pattern.id;
    setClip(clip, { resetHistory: false, activePatternId: activeId });
    const createdIndex = clip.patterns.findIndex(p => p.id === pattern.id);
    if (options?.patternLoop) {
      applyPatternLoop(options.patternLoop);
    } else if (options?.snapLoopToNew !== false && createdIndex >= 0) {
      applyPatternLoop(patternLoopAfterInsert(prevLoop, insertAt, prevCount));
    }
  }

  function arrangeRemovePattern(patternId: string): RemovedPatternPayload | null {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    if (clip.patterns.length <= 1) {
      setStatus("Keep at least one pattern.");
      return null;
    }
    const index = clip.patterns.findIndex(p => p.id === patternId);
    const pattern = index >= 0 ? clip.patterns[index] : undefined;
    if (!pattern || index < 0) {
      return null;
    }
    const prevActivePatternId = tracker.getActivePatternId();
    const prevPatternLoop = clampPatternLoop(uiStore.getValue().patternLoop, clip.patterns.length);
    const payload: RemovedPatternPayload = {
      pattern: structuredClone(pattern),
      index,
      prevActivePatternId,
      prevPatternLoop,
    };
    clip.patterns = clip.patterns.filter(p => p.id !== patternId);
    syncLinearArrangement(clip);
    const nextId = prevActivePatternId === patternId
      ? (clip.patterns[0]?.id ?? "P0")
      : prevActivePatternId;
    setClip(clip, { resetHistory: false, activePatternId: nextId });
    const nextLoop = applyPatternLoop(uiStore.getValue().patternLoop);
    payload.nextActivePatternId = nextId;
    payload.nextPatternLoop = nextLoop;
    return payload;
  }

  function arrangeRestorePattern(payload: RemovedPatternPayload): void {
    clip = tracker.getClip();
    normalizeClipScenes(clip);
    const insertAt = Math.max(0, Math.min(payload.index, clip.patterns.length));
    clip.patterns.splice(insertAt, 0, structuredClone(payload.pattern));
    syncLinearArrangement(clip);
    setClip(clip, {
      resetHistory: false,
      activePatternId: payload.prevActivePatternId,
    });
    applyPatternLoop(payload.prevPatternLoop);
  }

  function arrangeRenamePattern(patternId: string, name: string | undefined): void {
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    if (!pattern) {
      return;
    }
    if (name) {
      pattern.name = name.slice(0, 48);
    } else {
      delete pattern.name;
    }
    setClip(clip, { resetHistory: false, activePatternId: patternId });
  }

  function arrangeSetPatternLoopLive(range: PatternLoopRange): void {
    clip = tracker.getClip();
    const next = applyPatternLoop(range);
    const ids = patternIdsInLoop(clip.patterns, next);
    if (!ids.includes(tracker.getActivePatternId()) && ids[0]) {
      tracker.setActivePatternId(ids[0]);
      patchUi({ activePatternId: ids[0] });
      persist.schedule({ panels: { activePatternId: ids[0], patternLoop: next } });
    }
    if (session?.isMusicPlaying()) {
      stopPlayback();
    }
    setStatus(
      next.length === 1
        ? `Loop brace · ${ids[0]}`
        : `Loop brace · ${ids[0]}…${ids[ids.length - 1]} (${next.length} patterns)`,
    );
  }

  function arrangeSetTrackCells(
    patternId: string,
    track: number,
    cells: import("../../../../src/lib/audio").PatternCell[],
  ): void {
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    const dest = pattern?.tracks[track];
    if (!pattern || !dest) {
      return;
    }
    dest.cells = fitCellsToSteps(cells, patternSteps(pattern));
    setClip(clip, { resetHistory: false, activePatternId: patternId });
  }

  function arrangeReplacePattern(
    patternId: string,
    next: import("../../../../src/lib/audio").Pattern,
  ): void {
    clip = tracker.getClip();
    const index = clip.patterns.findIndex(p => p.id === patternId);
    if (index < 0) {
      return;
    }
    const copy = structuredClone(next);
    copy.id = patternId;
    clip.patterns[index] = copy;
    syncLinearArrangement(clip);
    setClip(clip, { resetHistory: false, activePatternId: patternId });
  }

  function arrangeResizePattern(patternId: string, steps: number): void {
    clip = tracker.getClip();
    const pattern = findPattern(clip, patternId);
    if (!pattern) {
      return;
    }
    resizePatternSteps(pattern, steps);
    setClip(clip, { resetHistory: false, activePatternId: patternId });
  }

  function arrangeSetBpm(bpm: number): void {
    clip = tracker.getClip();
    clip.bpm = bpm;
    setClip(clip, { resetHistory: false });
    session?.setBpm(clip.bpm);
  }

  function arrangeSetStepDivision(stepDivision: number): void {
    clip = tracker.getClip();
    clip.stepDivision = stepDivision;
    setClip(clip, { resetHistory: false });
  }

  registerAdamArrangeBridge({
    getActivePatternId: () => tracker.getActivePatternId(),
    getPatternLoop: () => currentPatternLoop(tracker.getClip()),
    setPatternLoop: arrangeSetPatternLoopLive,
    focusPattern: (patternId, loop) => {
      clip = tracker.getClip();
      applyPatternLoop(loop);
      if (findPattern(clip, patternId)) {
        tracker.setActivePatternId(patternId);
        patchUi({ activePatternId: patternId });
        persist.schedule({ panels: { activePatternId: patternId, patternLoop: loop } });
        syncStepsField();
        syncArrangeCursorFromTracker();
      }
    },
    getPattern: patternId => {
      const found = findPattern(tracker.getClip(), patternId);
      return found ? structuredClone(found) : undefined;
    },
    renamePattern: arrangeRenamePattern,
    insertPatternAfter: arrangeInsertPatternAfter,
    removePattern: arrangeRemovePattern,
    restorePattern: arrangeRestorePattern,
    reorderPatterns: (fromIndex, toIndex) => {
      clip = tracker.getClip();
      if (!reorderPatternsInClip(clip, fromIndex, toIndex)) {
        return false;
      }
      setClip(clip, { resetHistory: false });
      applyPatternLoop(uiStore.getValue().patternLoop);
      return true;
    },
    setTrackCells: arrangeSetTrackCells,
    replacePattern: arrangeReplacePattern,
    resizePattern: arrangeResizePattern,
    getBpm: () => tracker.getClip().bpm,
    setBpm: arrangeSetBpm,
    getStepDivision: () => tracker.getClip().stepDivision,
    setStepDivision: arrangeSetStepDivision,
    getClipLoop: () => tracker.getClip().loop === true,
    setClipLoop: applyClipLoopLive,
  });

  registerAdamInstrumentBridge({
    getInstrument: slot => {
      const instrument = projectStore.getValue().instruments[slot];
      return instrument ? cloneInstrument(instrument) : undefined;
    },
    setInstrument: (slot, instrument) => {
      applyInstrumentLive(slot, instrument);
    },
    getSynthBankSnapshot: captureSynthBankSnapshot,
    applySynthBankSnapshot: applySynthBankSnapshotLive,
    getWavetableBankSnapshot: captureWtBankSnapshot,
    applyWavetableBankSnapshot: applyWtBankSnapshotLive,
    getSamplerBankSnapshot: captureSamplerBankSnapshot,
    applySamplerBankSnapshot: applySamplerBankSnapshotLive,
    getDrumBank: () => structuredClone(projectStore.getValue().drumBank),
    setDrumBank: entries => {
      const next = sanitizeDrumBank(entries);
      patchProject({ drumBank: next });
      persist.schedule({ drumBank: next });
    },
  });

  const sampleNameCache = new Map<string, string>();
  registerAdamSampleBridge({
    renameSample(id, name) {
      const updated = renameSampleAssetSync(id, name);
      sampleNameCache.set(id, updated.name);
      setStatus(`Renamed sample → “${updated.name}”`);
    },
    getSampleName(id) {
      return sampleNameCache.get(id);
    },
    applyDeleteSample(payload) {
      forgetSampleAssetSync(payload.meta.id);
      sampleNameCache.delete(payload.meta.id);
      setStatus(`Deleted sample “${payload.meta.name}” · ⌘Z to undo`);
    },
    restoreSample(payload) {
      const meta = restoreSampleAssetSync(payload);
      sampleNameCache.set(meta.id, meta.name);
      setStatus(`Restored sample “${meta.name}”`);
    },
    moveSample(id, folderId, sortKey) {
      const updated = moveSampleToFolderSync(id, folderId, sortKey);
      setStatus(
        folderId
          ? `Moved “${updated.name}” into folder`
          : `Moved “${updated.name}” to Library`,
      );
    },
    createFolder(draft) {
      const folder = createSampleFolderSync({
        id: draft.id,
        name: draft.name,
        parentId: draft.parentId,
        createdAt: draft.createdAt,
        sortKey: draft.sortKey,
      });
      setStatus(`Created folder “${folder.name}”`);
      return folder;
    },
    renameFolder(id, name) {
      const updated = renameSampleFolderSync(id, name);
      setStatus(`Renamed folder → “${updated.name}”`);
    },
    moveFolder(id, parentId, sortKey) {
      const updated = moveSampleFolderSync(id, parentId, sortKey);
      setStatus(`Moved folder “${updated.name}”`);
    },
    applyDeleteFolder(payload) {
      forgetSampleFolderSync(payload);
      for (const sample of payload.samples) {
        sampleNameCache.delete(sample.meta.id);
      }
      const n = payload.samples.length;
      setStatus(
        n > 0
          ? `Deleted folder · ${n} sample${n === 1 ? "" : "s"} · ⌘Z to undo`
          : `Deleted folder · ⌘Z to undo`,
      );
    },
    restoreFolder(payload) {
      restoreSampleFolderSync(payload);
      for (const sample of payload.samples) {
        sampleNameCache.set(sample.meta.id, sample.meta.name);
      }
      setStatus(`Restored folder`);
    },
  });

  function removeArrayIndex<T>(list: readonly T[] | undefined, index: number): T[] {
    const next = list ? [...list] : [];
    if (index >= 0 && index < next.length) {
      next.splice(index, 1);
    }
    return next;
  }

  async function exportProject(): Promise<void> {
    const {
      presets, bank, drumBank, wavetableBank, samplerBank,
      fx, instruments, slotMix, slotFx, muted, solo, trackColors,
    } = projectStore.getValue();
    const snapshot = {
      clip: tracker.getClip(),
      instruments,
      presets,
      bank,
      drumBank,
      wavetableBank,
      samplerBank,
      slotMix,
      slotFx,
      muted,
      solo,
      trackColors,
      fx,
      patternLoop: currentPatternLoop(),
      controlBindings: controlSessionStore.getValue().bindings,
    };
    const sampleIds = new Set<string>();
    for (const instrument of instruments) {
      if (instrument.kind === "sampler") {
        for (const id of samplerPresetSampleIds(instrument.preset)) {
          sampleIds.add(id);
        }
      } else if (instrument.kind === "drums") {
        for (const id of drumKitSampleIds(instrument.kit)) {
          sampleIds.add(id);
        }
      }
    }
    for (const entry of samplerBank) {
      for (const id of samplerPresetSampleIds(entry.preset)) {
        sampleIds.add(id);
      }
    }
    for (const entry of drumBank) {
      for (const id of drumKitSampleIds(entry.kit)) {
        sampleIds.add(id);
      }
    }
    const samples = await collectSamplesForExport([...sampleIds]);
    const folders = await collectFoldersForExport(samples.map(s => s.meta));
    const bytes = serializeAdamPackage(snapshot, { samples, folders });
    const project = buildLabProject(snapshot);
    downloadBytes(bytes, `project${ADAM_FILE_EXTENSION}`, ADAM_MIME_TYPE);
    setStatus(
      `Exported .adam · ${project.presets.length} slots · ${samples.length} sample${samples.length === 1 ? "" : "s"} · ${project.fx.length} FX`,
    );
  }

  function exportMidi(): void {
    const clip = tracker.getClip();
    syncLinearArrangement(clip);
    const { instruments } = projectStore.getValue();
    const exported = exportMidiFromProject(clip, instruments, {
      song: true,
      name: "adam-arrangement",
    });
    downloadMidi(exported.bytes, "adam-arrangement.mid");
    const warnText = exported.warnings.length > 0 ? ` · ${exported.warnings[0]}` : "";
    setStatus(
      `Exported MIDI · ${exported.trackCount} track${exported.trackCount === 1 ? "" : "s"} · ${exported.noteCount} notes${warnText}`,
    );
  }

  async function importProject(): Promise<void> {
    try {
      const { bytes, name } = await pickAdamProjectFile();
      let next;
      let packedSamples: Awaited<ReturnType<typeof deserializeAdamPackage>>["samples"] = [];
      let packedFolders: Awaited<ReturnType<typeof deserializeAdamPackage>>["folders"] = [];
      if (looksLikeZip(bytes) || name.toLowerCase().endsWith(ADAM_FILE_EXTENSION)) {
        const opened = deserializeAdamPackage(bytes);
        next = opened.snapshot;
        packedSamples = opened.samples;
        packedFolders = opened.folders;
      } else {
        // Legacy plain JSON project export.
        next = parseLabProject(JSON.parse(strFromU8(bytes)));
      }
      if (packedFolders.length > 0) {
        await ingestFoldersFromPackage(packedFolders);
      }
      for (const sample of packedSamples) {
        await ingestSampleFromPackage(sample.meta, sample.pcm);
      }
      if (session?.isMusicPlaying()) {
        stopPlayback();
      }
      const {
        bank: previousBank,
        drumBank: previousDrumBank,
        wavetableBank: previousWavetableBank,
        samplerBank: previousSamplerBank,
      } = projectStore.getValue();
      const bank = mergeBanksPreservingUser(next.bank, previousBank);
      const drumBank = mergeDrumBanksPreservingUser(next.drumBank, previousDrumBank);
      const wavetableBank = mergeWavetableBanksPreservingUser(
        next.wavetableBank,
        previousWavetableBank,
      );
      const samplerBank = mergeSamplerBanksPreservingUser(
        next.samplerBank,
        previousSamplerBank,
      );
      const solo = normalizeMuteSoloFlags(next.solo);
      const muted = adoptMuteSolo(
        next.clip,
        next.muted,
        solo,
        next.clip.patterns[0]?.id,
      );
      setClip(next.clip);
      activeSceneId = next.clip.scenes[0]?.id ?? "S0";
      slotBankIds = syncSlotBankIdsFromPresets({
        presets: next.presets,
        bank,
        slotBankIds: initialSlotBankIds(),
      }).slotBankIds;
      wtSlotBankIds = syncWtSlotBankIdsFromInstruments({
        instruments: next.instruments,
        bank: wavetableBank,
        slotBankIds: initialWtSlotBankIds(next.instruments.length),
      }).slotBankIds;
      samplerSlotBankIds = syncSamplerSlotBankIdsFromInstruments({
        instruments: next.instruments,
        bank: samplerBank,
        slotBankIds: initialSamplerSlotBankIds(next.instruments.length),
      }).slotBankIds;
      void pushAllInstrumentsAsync(next.instruments);
      const trackColors = normalizeTrackColors(
        next.trackColors,
        next.instruments.length,
      );
      patchProject({
        instruments: next.instruments,
        presets: next.presets,
        bank,
        drumBank,
        wavetableBank,
        samplerBank,
        slotMix: next.slotMix,
        slotFx: next.slotFx,
        muted,
        solo,
        trackColors,
        fx: next.fx,
      });
      tracker.setTrackColors(trackColors);
      pushLiveFxStack(next.fx);
      engine?.transport.setSlotMixes(next.slotMix);
      pushLiveSlotFxStacks(next.slotFx ?? []);
      const importedLoop = sanitizePatternLoop(next.patternLoop, next.clip.patterns.length);
      applyPatternLoop(importedLoop);
      setControlBindings(next.controlBindings ?? []);
      persist.schedule({
        clip: next.clip,
        instruments: next.instruments,
        presets: next.presets,
        bank,
        drumBank,
        wavetableBank,
        samplerBank,
        slotMix: next.slotMix,
        slotFx: next.slotFx,
        muted,
        solo,
        trackColors,
        fx: next.fx,
        panels: { activeSceneId, patternLoop: importedLoop },
      });
      patchUi({ dslWarnings: "" });
      const bindingCount = next.controlBindings?.length ?? 0;
      setStatus(
        `Imported ${name.toLowerCase().endsWith(ADAM_FILE_EXTENSION) ? ".adam" : "project"} · ${next.presets.length} slots · ${bank.length} bank · ${next.fx.length} FX${bindingCount > 0 ? ` · ${bindingCount} binding${bindingCount === 1 ? "" : "s"}` : ""}`,
      );
      tracker.focus();
    } catch (error) {
      if (error instanceof Error && error.message === "No file selected") {
        return;
      }
      setStatus(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function importMidi(): Promise<void> {
    try {
      const { buffer, name } = await pickMidiFile();
      const imported = importMidiToProject(buffer);
      if (session?.isMusicPlaying()) {
        stopPlayback();
      }
      const instruments = imported.instruments;
      const trackCount = instruments.length;
      const muted = createDefaultMuteSolo(trackCount);
      const solo = createDefaultMuteSolo(trackCount);
      const slotMix = createDefaultSlotMixes(trackCount);
      const slotFx = createDefaultSlotFxStacks(trackCount);
      activeSceneId = "S0";
      slotBankIds = initialSlotBankIds();
      while (slotBankIds.length < trackCount) {
        slotBankIds.push(undefined);
      }
      void pushAllInstrumentsAsync(instruments);
      const trackColors = normalizeTrackColors(null, trackCount);
      patchProject({
        instruments,
        presets: presetsFromInstruments(instruments),
        slotMix,
        slotFx,
        muted,
        solo,
        trackColors,
      });
      tracker.setMuteSolo(muted, solo, { emit: false });
      tracker.setTrackColors(trackColors);
      patchUi({ trackMuted: muted, trackSolo: solo });
      syncSessionMuteSolo(solo, trackCount);
      engine?.transport.setSlotMixes(slotMix);
      pushLiveSlotFxStacks(slotFx);
      setClip(imported.clip, { resetHistory: true, activePatternId: "P0" });
      persist.schedule({
        clip: imported.clip,
        instruments,
        slotMix,
        slotFx,
        muted,
        solo,
        trackColors,
        panels: { activeSceneId: "S0" },
      });
      const warnText = imported.warnings.length > 0
        ? ` · ${imported.warnings.slice(0, 3).join(" · ")}`
        : "";
      patchUi({ dslWarnings: imported.warnings.join("\n") });
      setStatus(
        `Imported MIDI · ${trackCount} track${trackCount === 1 ? "" : "s"} · ${imported.clip.patterns[0]?.steps ?? 0} steps${warnText}`,
      );
      tracker.focus();
    } catch (error) {
      if (error instanceof Error && error.message === "No file selected") {
        return;
      }
      setStatus(`MIDI import failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async function bounceToWav(scope: PlayScope): Promise<void> {
    if (!engine || bouncing) {
      return;
    }
    stopPlayback();
    const full = tracker.getClip();
    if (scope === "project") {
      syncLinearArrangement(full);
    } else {
      normalizeClipScenes(full);
    }
    const bounceClip = scopedPlayClip(full, scope);
    const instruments = projectStore.getValue().instruments;
    const effects = projectStore.getValue().fx;
    const label = bounceScopeLabel(scope);
    setBouncing(true, "Bouncing to WAV…");
    setStatus(`Bouncing ${label} offline…`);
    try {
      const result = await bounceClipOffline({
        clip: bounceClip,
        instruments,
        slotMix: projectStore.getValue().slotMix,
        slotFx: projectStore.getValue().slotFx,
        effects,
        // Per-pattern mute is on the clip; keep the global mask clear.
        muted: createDefaultMuteSolo(instruments.length),
        solo: tracker.getSolo(),
        chunkSeconds: 0.2,
        onProgress: setBounceProgress,
        resolveSample: resolveSampleRuntime,
        assetsBaseUrl: labAssetsBaseUrl(),
      });
      const wav = encodeWavPcm16(result.samples, result.sampleRate, 2);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadWav(wav, `adam-bounce-${label}-${stamp}.wav`);
      setStatus(
        `Bounced ${label} · ${result.durationSec.toFixed(1)}s WAV `
        + `(${result.patternSec.toFixed(1)}s + tail) · ${result.sampleRate} Hz stereo`,
      );
    } catch (error) {
      setStatus(`Bounce failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setBouncing(false);
    }
  }

  /** Bounce the loop brace offline into a new sampler track and open Author. */
  async function bounceToSampler(): Promise<void> {
    if (!engine || bouncing) {
      return;
    }
    if (projectStore.getValue().instruments.length >= MAX_INSTRUMENT_SLOT_COUNT) {
      setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
      return;
    }
    stopPlayback();
    const full = tracker.getClip();
    normalizeClipScenes(full);
    const scope: PlayScope = "range";
    const bounceClip = scopedPlayClip(full, scope);
    const instruments = projectStore.getValue().instruments;
    const label = bounceScopeLabel(scope);
    const sampleName = `Bounce ${label}`;
    setBouncing(true, "Bounce to sampler…");
    setStatus(`Bouncing ${label} → sampler…`);
    try {
      const result = await bounceClipOffline({
        clip: bounceClip,
        instruments,
        slotMix: projectStore.getValue().slotMix,
        slotFx: projectStore.getValue().slotFx,
        // Sampler bounce keeps per-track inserts but skips the master bus —
        // the sample should stay usable under whatever master FX the project uses later.
        effects: [],
        muted: createDefaultMuteSolo(instruments.length),
        solo: tracker.getSolo(),
        chunkSeconds: 0.2,
        onProgress: setBounceProgress,
        resolveSample: resolveSampleRuntime,
        assetsBaseUrl: labAssetsBaseUrl(),
      });
      const mono = interleavedStereoToMono(result.samples);
      const added = applyAddTrack();
      if (!added) {
        return;
      }
      const slot = added.slot as InstrumentSlot;
      commitInstrument(slot, samplerInstrument(createDefaultSamplerPreset()));
      await ingestDecodedSampler(slot, {
        name: sampleName,
        pcm: mono,
        sampleRate: result.sampleRate,
        source: "bounce",
      });
      tracker.renameTrack(slot, sampleName);
      openInstrument(slot);
      patchUi({ authorOpen: true });
      setStatus(
        `Bounce → sampler I${slot + 1} · ${result.durationSec.toFixed(1)}s `
        + `(${result.patternSec.toFixed(1)}s + tail) · Author open`,
      );
    } catch (error) {
      setStatus(
        `Bounce to sampler failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      setBouncing(false);
    }
  }

  let transportMic: MicRecorder | null = null;

  function discardTransportMic(): void {
    if (!transportMic) {
      clearTransportRecordMeter();
      patchTransport({ micRecording: false });
      return;
    }
    try {
      transportMic.stop();
    } catch {
      // ignore
    }
    transportMic = null;
    clearTransportRecordMeter();
    patchTransport({ micRecording: false });
  }

  /**
   * Transport Record: mic → new sampler track (overdub).
   * Does not stop playback — record against a running arrangement, or arm
   * dry if transport is stopped. Auto-enables audio; exclusive with Author mic.
   */
  async function toggleMicRecord(): Promise<void> {
    if (transportMic) {
      const sampleRate = engine?.context?.sampleRate ?? 48_000;
      const pcm = transportMic.stop();
      transportMic = null;
      clearTransportRecordMeter();
      patchTransport({ micRecording: false });
      if (pcm.length < 256) {
        setStatus("Recording too short");
        return;
      }
      if (projectStore.getValue().instruments.length >= MAX_INSTRUMENT_SLOT_COUNT) {
        setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
        return;
      }
      const sampleName = `Record ${new Date().toLocaleTimeString()}`;
      try {
        const added = applyAddTrack();
        if (!added) {
          return;
        }
        const slot = added.slot as InstrumentSlot;
        commitInstrument(slot, samplerInstrument(createDefaultSamplerPreset()));
        await ingestDecodedSampler(slot, {
          name: sampleName,
          pcm,
          sampleRate,
          source: "record",
        });
        tracker.renameTrack(slot, sampleName);
        setStatus(
          `Record → sampler I${slot + 1} · ${(pcm.length / sampleRate).toFixed(1)}s`,
        );
      } catch (error) {
        setStatus(
          `Record failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
      return;
    }

    if (transportStore.getValue().authorMicRecording) {
      setStatus("Stop Sampler Author recording first");
      return;
    }
    if (bouncing) {
      return;
    }
    if (projectStore.getValue().instruments.length >= MAX_INSTRUMENT_SLOT_COUNT) {
      setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
      return;
    }

    try {
      if (!engine?.context || !transportStore.getValue().armed) {
        await enableAudio();
      }
      const ctx = engine?.context;
      if (!ctx) {
        setStatus("Audio engine not ready");
        return;
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      transportMic = await startMicCapture(ctx);
      patchTransport({ micRecording: true });
      const playing = transportStore.getValue().playing;
      setStatus(
        playing
          ? "Recording over arrangement… press Record again to finish → new sampler"
          : "Recording (transport stopped)… press Play to hear patterns, Record again to finish",
      );
    } catch (error) {
      discardTransportMic();
      setStatus(
        `Mic record failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  async function bounceStems(scope: PlayScope): Promise<void> {
    if (!engine || bouncing) {
      return;
    }
    stopPlayback();
    const full = tracker.getClip();
    if (scope === "project") {
      syncLinearArrangement(full);
    } else {
      normalizeClipScenes(full);
    }
    const bounceClip = scopedPlayClip(full, scope);
    const instruments = projectStore.getValue().instruments;
    const effects = projectStore.getValue().fx;
    const label = bounceScopeLabel(scope);
    setBouncing(true, "Exporting stems…");
    setStatus(`Bouncing ${label} stems offline…`);
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const result = await bounceStemsToZip({
        clip: bounceClip,
        instruments,
        slotMix: projectStore.getValue().slotMix,
        slotFx: projectStore.getValue().slotFx,
        effects,
        chunkSeconds: 0.2,
        onProgress: setBounceProgress,
        filename: `adam-stems-${label}-${stamp}.zip`,
        resolveSample: resolveSampleRuntime,
        assetsBaseUrl: labAssetsBaseUrl(),
      });
      setStatus(
        `Stems ${label} · ${result.stems.length} tracks · ${result.durationSec.toFixed(1)}s `
        + `ZIP (${result.patternSec.toFixed(1)}s + tail) · ${result.sampleRate} Hz stereo`,
      );
    } catch (error) {
      setStatus(`Stems failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setBouncing(false);
    }
  }

  function bounceScopeLabel(scope: PlayScope): string {
    if (scope === "pattern" || scope === "range") {
      const loop = currentPatternLoop();
      const ids = patternIdsInLoop(clip.patterns, loop);
      if (ids.length <= 1) {
        return ids[0] ?? tracker.getActivePatternId();
      }
      return `${ids[0]}-${ids[ids.length - 1]}`;
    }
    if (scope === "scene") {
      return activeSceneId;
    }
    return "project";
  }

  function onPageHide(): void {
    persist.flush();
  }
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("beforeunload", onPageHide);

  setClip(clip, {
    persist: false,
    activePatternId: bootActivePatternId,
  });
  // Tracker starts with an empty clip; apply the brace only after setClip so
  // pattern ids resolve and the stacked grid matches the persisted loop.
  applyPatternLoop(bootPatternLoop, { persist: false });
  if (
    findPattern(clip, bootActivePatternId)
    && patternLoopContainsIndex(
      bootPatternLoop,
      clip.patterns.findIndex(p => p.id === bootActivePatternId),
      clip.patterns.length,
    )
  ) {
    tracker.setActivePatternId(bootActivePatternId);
  }
  if (migratedMute) {
    persist.schedule({ clip, muted: bootMuted });
  }
  setArmed(false);

  if (saved.panels?.patternsOpen) {
    patchUi({ patternsOpen: true });
  }
  if (saved.panels?.samplesOpen) {
    patchUi({ samplesOpen: true });
  }
  if (saved.panels?.fxOpen) {
    patchUi({ fxOpen: true });
  }

  setDslOpen(Boolean(saved.panels?.dslOpen), { persist: false });
  setStatusOpen(Boolean(saved.panels?.statusOpen), { persist: false });

  function setFollowPlayhead(on: boolean, options?: { persist?: boolean }): void {
    tracker.setFollowPlayhead(on);
    patchTransport({ followPlayhead: on });
    if (options?.persist !== false) {
      persist.schedule({ panels: { followPlayhead: on } });
    }
  }

  function setArmMode(on: boolean, options?: { persist?: boolean }): void {
    tracker.setArmMode(on);
    patchTransport({ armMode: on });
    if (options?.persist !== false) {
      persist.schedule({ panels: { armMode: on } });
    }
  }

  function toggleArmModeAction(): void {
    const next = !transportStore.getValue().armMode;
    setArmMode(next);
    setStatus(next ? "Arm on — piano keys write notes" : "Arm off");
    if (next) {
      tracker.focus();
    }
  }

  function onToggleArmKey(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
      return;
    }
    if (event.key.toLowerCase() !== "k") {
      return;
    }
    if (bouncing) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    toggleArmModeAction();
  }
  window.addEventListener("keydown", onToggleArmKey, true);

  /** Esc exits arm mode (modals / text fields keep their own Esc handling). */
  function onArmEscapeKey(event: KeyboardEvent): void {
    if (event.key !== "Escape") {
      return;
    }
    if (!transportStore.getValue().armMode) {
      return;
    }
    if (bouncing) {
      return;
    }
    const ui = uiStore.getValue();
    if (
      ui.authorOpen
      || ui.helpOpen
      || ui.goToOpen
      || ui.generateNotesOpen
      || ui.mixerOpen
      || ui.performanceOpen
      || ui.settingsOpen
      || ui.rowLabelEdit
      || ui.trackLabelEdit
    ) {
      return;
    }
    if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) {
      return;
    }
    event.preventDefault();
    setArmMode(false);
    setStatus("Arm off");
  }
  window.addEventListener("keydown", onArmEscapeKey, true);

  /** ⌘/Ctrl+Shift+I / F / A / P / L / M — instruments / FX / arrange / performance / samples / mixer. */
  function onTogglePanelKey(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || !event.shiftKey || event.altKey) {
      return;
    }
    // Prefer `code` so Shift does not depend on `key` letter casing / layout.
    const code = event.code;
    if (
      code !== "KeyI"
      && code !== "KeyF"
      && code !== "KeyA"
      && code !== "KeyP"
      && code !== "KeyL"
      && code !== "KeyM"
    ) {
      return;
    }
    event.preventDefault();
    if (bouncing) {
      return;
    }
    if (code === "KeyI") {
      toggleInstrument();
    } else if (code === "KeyF") {
      toggleFx();
    } else if (code === "KeyA") {
      togglePatterns();
    } else if (code === "KeyP") {
      togglePerformance();
    } else if (code === "KeyL") {
      toggleSamples();
    } else {
      toggleMixer();
    }
  }
  window.addEventListener("keydown", onTogglePanelKey, true);

  /** ⌘/Ctrl+I — toggle Author for tracker cursor track (if focused) or instruments panel slot. */
  function onOpenAuthorKey(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) {
      return;
    }
    if (event.code !== "KeyI") {
      return;
    }
    if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) {
      return;
    }
    event.preventDefault();
    if (bouncing) {
      return;
    }
    openInstrumentAuthor();
  }
  window.addEventListener("keydown", onOpenAuthorKey, true);

  /** Global ⌘Z / ⌘⇧Z / ⌘Y — works with no focus; skips text fields. */
  function onUndoRedoKey(event: KeyboardEvent): void {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) {
      return;
    }
    const key = event.key.toLowerCase();
    if (key !== "z" && key !== "y") {
      return;
    }
    if (bouncing) {
      event.preventDefault();
      return;
    }
    if (isTypingTarget(event.target) || isTypingTarget(document.activeElement)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const redo = key === "y" || (key === "z" && event.shiftKey);
    const result = redo ? adamRedo() : adamUndo();
    if (!result) {
      setStatus(redo ? "Nothing to redo" : "Nothing to undo");
      return;
    }
    setStatus(redo ? "Redo" : "Undo");
  }
  window.addEventListener("keydown", onUndoRedoKey, true);

  setFollowPlayhead(Boolean(saved.panels?.followPlayhead), { persist: false });
  setArmMode(Boolean(saved.panels?.armMode), { persist: false });

  setLabActions({
    enableAudio,
    getTelemetry: () => engine?.getTelemetry() ?? createEmptyTelemetry(),
    play: startPlayback,
    stop: stopPlayback,
    toggleFollowPlayhead() {
      const next = !tracker.isFollowPlayhead();
      setFollowPlayhead(next);
      setStatus(next ? "Follow playhead on" : "Follow playhead off");
    },
    toggleArmMode: toggleArmModeAction,
    toggleLoop() {
      setClipLoop(!clip.loop);
    },
    setBpm: setBpmFromUi,
    setGrid: setGridFromUi,
    setPatternLength(steps) {
      applyPatternLength(steps || activePatternSteps());
    },
    toggleInstrument,
    togglePatterns,
    toggleSamples,
    toggleFx,
    selectPattern: selectActivePattern,
    setPatternLoop,
    getPatternLoop: () => currentPatternLoop(),
    createPattern,
    renamePattern,
    deletePattern,
    duplicatePattern,
    reorderPatterns,
    copyPattern,
    cutPattern,
    pastePattern,
    copyTrack,
    pasteTrack,
    copyInstrument,
    pasteInstrument,
    openPatternTrack,
    selectScene: selectActiveScene,
    createScene,
    renameScene,
    deleteScene,
    setScenePatternOrder,
    playScene: startScenePlayback,
    newProject,
    addTrack,
    removeTrack,
    duplicateTrack,
    reorderTracks,
    importProject,
    importMidi,
    exportMidi,
    exportProject,
    bounce: bounceToWav,
    bounceStems,
    bounceToSampler,
    toggleMicRecord,
    getMicRecordLevel: () => transportMic?.getLevel() ?? 0,
    loadDemo,
    applyDsl() {
      applyDslFromTextarea();
      tracker.focus();
    },
    applyGridToDsl() {
      setClip(tracker.getClip(), { syncDsl: true, syncGrid: true, resetHistory: false });
      patchUi({ dslWarnings: "" });
      setStatus("Grid applied → DSL.");
    },
    toggleDslPanel() {
      const open = !uiStore.getValue().dslOpen;
      setDslOpen(open);
      setStatus(open ? "Pattern DSL open" : "Pattern DSL closed");
    },
    toggleStatusPanel() {
      setStatusOpen(!uiStore.getValue().statusOpen);
    },
    setMaterial(mode) {
      setMaterialMode(mode);
    },
    toggleHelp() {
      const open = !uiStore.getValue().helpOpen;
      patchUi({ helpOpen: open });
      if (open) {
        setStatus("Tracker help open — drag the title bar to move.");
      }
    },
    toggleGoTo() {
      const open = !uiStore.getValue().goToOpen;
      patchUi({ goToOpen: open });
      if (open) {
        setStatus("Go to — type a row number or marker name.");
      }
    },
    toggleGenerateNotes() {
      const open = !uiStore.getValue().generateNotesOpen;
      patchUi({ generateNotesOpen: open });
      if (open) {
        setStatus("Generate notes — chords arpeggiate into the selection.");
      }
    },
    toggleMixer,
    toggleSettings,
    togglePerformance,
    startPerformance,
    stopPerformance,
    togglePerformanceRecord,
    launchPerformanceCell,
    launchPerformancePattern,
    stopPerformanceLane,
    applyGenerateNotes(rowNotes) {
      const ok = tracker.applySelectionGeneratedNotes(rowNotes);
      if (ok) {
        setStatus("Generated notes into selection");
      }
    },
    goToRow(step) {
      // Suggestions use active-pattern local rows; the grid cursor is global in a stacked view.
      const global =
        tracker.locateGlobalStep(tracker.getActivePatternId(), step) ?? step;
      tracker.jumpToCell(global);
      setStatus(`Jumped to row ${step}`);
    },
    focusTracker() {
      tracker.focus();
    },
    openRowLabel(step, marker, anchor) {
      patchUi({
        rowLabelEdit: {
          step,
          label: marker?.label ?? "",
          color: marker?.color ?? 0,
          anchor,
        },
      });
    },
    commitRowLabel(step, marker) {
      tracker.applyRowMarker(step, marker ?? undefined);
    },
    openTrackLabel(track, color, anchor) {
      patchUi({
        trackLabelEdit: {
          track,
          color,
          anchor,
        },
      });
    },
    setTrackColor(track, color) {
      const n = Math.max(projectStore.getValue().instruments.length, track + 1);
      const from = normalizeTrackColors(projectStore.getValue().trackColors, n)[track] ?? null;
      const to = color == null ? null : clampRowMarkerColor(color);
      if (from === to) {
        return;
      }
      getAdamCommandHost().execute(new SetTrackColorCommand(track, from, to));
    },
    applyFxStack,
    applySlotFxStack,
    getFxStack: () => projectStore.getValue().fx,
    setFxBypass,
    setFxScope,
    persistPanels(panels) {
      persist.schedule({ panels });
    },
    setStatus,
    openInstrument,
    closeInstrument,
    setInstrumentSlot(slot) {
      lastInstrumentSlot = slot;
      if (uiStore.getValue().instrumentSlot != null) {
        patchUi({ instrumentSlot: slot });
        persist.schedule({ panels: { instrumentSlot: slot, synthSlot: slot } });
      }
    },
    openInstrumentAuthor,
    closeInstrumentAuthor() {
      patchUi({ authorOpen: false });
    },
    closeArrange() {
      patchUi({ patternsOpen: false });
    },
    updateInstrument(slot, instrument) {
      commitInstrument(slot, instrument);
    },
    updateInstrumentPreset(slot, preset) {
      commitInstrument(slot, synthInstrument(preset));
    },
    updateInstrumentKit(slot, kit) {
      commitInstrument(slot, drumsInstrument(kit));
    },
    updateInstrumentSf2Patch(slot, patch) {
      commitInstrument(slot, sf2Instrument(normalizeSf2Patch(patch)));
    },
    setSlotMix(slot, partial) {
      const n = Math.max(projectStore.getValue().instruments.length, slot + 1);
      const from = normalizeSlotMix(
        normalizeSlotMixes(projectStore.getValue().slotMix, n)[slot],
      );
      const to = normalizeSlotMix({ ...from, ...partial });
      if (mixEqual(from, to)) {
        return;
      }
      getAdamCommandHost().execute(new SetSlotMixCommand(slot, from, to));
    },
    renameTrack(slot, name) {
      tracker.renameTrack(slot, name);
      setStatus(`Renamed track I${slot + 1} → “${name.trim() || `I${slot + 1}`}”`);
    },
    toggleMute(slot) {
      tracker.toggleMute(slot);
    },
    togglePatternMute(patternId, track) {
      if (tracker.getActivePatternId() === patternId) {
        tracker.toggleMute(track as InstrumentSlot);
        return;
      }
      const from = applyGetPatternMute(patternId, track);
      const to = !from;
      getAdamCommandHost().execute(
        new SetPatternMuteCommand(patternId, track, from, to),
      );
      setStatus(
        to
          ? `Muted ${patternId} · T${track + 1}`
          : `Unmuted ${patternId} · T${track + 1}`,
      );
    },
    toggleSolo(slot) {
      tracker.toggleSolo(slot);
    },
    setInstrumentKind(slot, kind) {
      if (kind === "drums") {
        commitInstrument(slot, drumsInstrument(createDefaultDrumKit()));
        setStatus(`Slot ${slot + 1} → Drums (Studio Kit)`);
        return;
      }
      if (kind === "sf2") {
        commitInstrument(slot, sf2Instrument(createDefaultSf2Patch()));
        setStatus(`Slot ${slot + 1} → SoundFont (loading…)`);
        return;
      }
      if (kind === "wavetable") {
        commitInstrument(slot, wavetableInstrument(createDefaultWavetablePreset()));
        setStatus(`Slot ${slot + 1} → Wavetable`);
        return;
      }
      if (kind === "sampler") {
        commitInstrument(slot, samplerInstrument(createDefaultSamplerPreset()));
        setStatus(`Slot ${slot + 1} → Sampler`);
        return;
      }
      if (kind === "externalMidi") {
        commitInstrument(slot, externalMidiInstrument(createDefaultExternalMidiPatch()));
        void externalMidiSink.ensureAccess();
        setStatus(`Slot ${slot + 1} → External MIDI`);
        return;
      }
      commitInstrument(slot, synthInstrument(DEFAULT_PRESETS[slot]!));
      setStatus(`Slot ${slot + 1} → Synth (${DEFAULT_PRESETS[slot]!.name})`);
    },
    resetInstrumentSlot(slot) {
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind === "drums") {
        commitInstrument(slot, drumsInstrument(createDefaultDrumKit()));
        setStatus(`Reset slot ${slot + 1} to Studio Kit`);
        return;
      }
      if (instrument?.kind === "sf2") {
        commitInstrument(slot, sf2Instrument(createDefaultSf2Patch()));
        setStatus(`Reset slot ${slot + 1} to default SoundFont`);
        return;
      }
      if (instrument?.kind === "wavetable") {
        commitInstrument(slot, wavetableInstrument(createDefaultWavetablePreset()));
        setStatus(`Reset slot ${slot + 1} to default Wavetable`);
        return;
      }
      if (instrument?.kind === "sampler") {
        commitInstrument(slot, samplerInstrument(createDefaultSamplerPreset()));
        setStatus(`Reset slot ${slot + 1} to default Sampler`);
        return;
      }
      if (instrument?.kind === "externalMidi") {
        commitInstrument(slot, externalMidiInstrument(createDefaultExternalMidiPatch()));
        setStatus(`Reset slot ${slot + 1} to External MIDI defaults`);
        return;
      }
      const result = resetSlotFactory(bankState(), slot);
      commitBankResult(result);
    },
    updateInstrumentExternalMidiPatch(slot, patch) {
      commitInstrument(slot, externalMidiInstrument(normalizeExternalMidiPatch(patch)));
    },
    listExternalMidiOutputs() {
      return externalMidiSink.listOutputs();
    },
    refreshExternalMidiAccess() {
      return externalMidiSink.ensureAccess();
    },
    updateInstrumentWavetablePreset(slot, preset) {
      commitInstrument(slot, wavetableInstrument(preset));
    },
    updateInstrumentSamplerPreset(slot, preset) {
      commitInstrument(slot, samplerInstrument(normalizeSamplerPreset(preset)));
    },
    async ensureSamplerSamples(slot) {
      const instrument = projectStore.getValue().instruments[slot];
      if (!engine || instrument?.kind !== "sampler") {
        return;
      }
      try {
        await pushSamplesForInstruments(engine, [instrument], resolveSampleRuntime);
      } catch (error) {
        setStatus(`Sample load failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    async ensureDrumSamples(slot) {
      const instrument = projectStore.getValue().instruments[slot];
      if (!engine || instrument?.kind !== "drums") {
        return;
      }
      try {
        await pushSamplesForInstruments(engine, [instrument], resolveSampleRuntime);
      } catch (error) {
        setStatus(`Sample load failed: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    async renameSample(id, name) {
      try {
        const runtime = await resolveSampleRuntime(id);
        if (!runtime) {
          setStatus("Sample not found");
          return;
        }
        const from = runtime.name;
        const to = name.trim() || from;
        if (to === from) {
          return;
        }
        sampleNameCache.set(id, from);
        getAdamCommandHost().execute(new RenameSampleCommand(id, from, to));
      } catch (error) {
        setStatus(
          `Rename failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async deleteSample(id) {
      try {
        const runtime = await resolveSampleRuntime(id);
        if (!runtime) {
          setStatus("Sample not found");
          return;
        }
        const meta = {
          id: runtime.id,
          name: runtime.name,
          sampleRate: runtime.sampleRate,
          channels: 1 as const,
          lengthFrames: runtime.pcm.length,
          rootKey: runtime.rootKey,
          source: "import" as const,
          createdAt: new Date().toISOString(),
          ...(runtime.oneCycle ? { oneCycle: true as const } : {}),
        };
        // Prefer catalog meta when present (keeps source / createdAt / folder).
        const catalogMeta = await getSampleAssetMeta(id);
        const payload: DeletedSamplePayload = {
          meta: catalogMeta ?? meta,
          pcm: (() => {
            const copy = new Float32Array(runtime.pcm.length);
            copy.set(runtime.pcm);
            return copy;
          })(),
        };
        getAdamCommandHost().execute(new DeleteSampleCommand(payload));
      } catch (error) {
        setStatus(
          `Delete failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async createSampleFolder(name, parentId = null) {
      try {
        await ensureSampleLibraryReady();
        const id =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `fld_${Date.now().toString(36)}`;
        getAdamCommandHost().execute(
          new CreateFolderCommand({
            id,
            name: name.trim() || "Folder",
            parentId,
            createdAt: new Date().toISOString(),
          }),
        );
        return id;
      } catch (error) {
        setStatus(
          `Create folder failed: ${error instanceof Error ? error.message : String(error)}`,
        );
        return undefined;
      }
    },
    async renameSampleFolder(id, name) {
      try {
        await ensureSampleLibraryReady();
        const folder = await getSampleFolderMeta(id);
        if (!folder) {
          setStatus("Folder not found");
          return;
        }
        const to = name.trim() || folder.name;
        if (to === folder.name) {
          return;
        }
        getAdamCommandHost().execute(new RenameFolderCommand(id, folder.name, to));
      } catch (error) {
        setStatus(
          `Rename folder failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async deleteSampleFolder(id) {
      try {
        await ensureSampleLibraryReady();
        const payload: DeletedFolderPayload = await captureDeletedFolderPayload(id);
        getAdamCommandHost().execute(new DeleteFolderCommand(payload));
      } catch (error) {
        setStatus(
          `Delete folder failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async moveSampleToFolder(sampleId, folderId) {
      try {
        await ensureSampleLibraryReady();
        const meta = await getSampleAssetMeta(sampleId);
        if (!meta) {
          setStatus("Sample not found");
          return;
        }
        const from = meta.folderId ?? null;
        const to = folderId;
        if (from === to) {
          return;
        }
        getAdamCommandHost().execute(
          new MoveSampleCommand(sampleId, from, to, meta.sortKey, undefined),
        );
      } catch (error) {
        setStatus(
          `Move sample failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async moveSampleFolder(folderId, parentId) {
      try {
        await ensureSampleLibraryReady();
        const folder = await getSampleFolderMeta(folderId);
        if (!folder) {
          setStatus("Folder not found");
          return;
        }
        const from = folder.parentId ?? null;
        const to = parentId;
        if (from === to) {
          return;
        }
        getAdamCommandHost().execute(
          new MoveFolderCommand(folderId, from, to, folder.sortKey, undefined),
        );
      } catch (error) {
        setStatus(
          `Move folder failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async duplicateSample(sampleId, folderId) {
      try {
        await ensureSampleLibraryReady();
        const runtime = await resolveSampleRuntime(sampleId);
        if (!runtime?.pcm.length) {
          setStatus("Sample missing — cannot duplicate");
          return undefined;
        }
        const baseName = (runtime.name || "Sample").trim() || "Sample";
        const meta = await ingestSamplePcm({
          name: `${baseName} copy`,
          pcm: runtime.pcm.slice(),
          sampleRate: runtime.sampleRate,
          rootKey: runtime.rootKey ?? 60,
          source: "import",
          folderId: folderId ?? null,
        });
        putCachedSample({
          id: meta.id,
          name: meta.name,
          sampleRate: meta.sampleRate,
          pcm: runtime.pcm.slice(),
          rootKey: meta.rootKey,
        });
        setStatus(`Duplicated “${meta.name}”`);
        return meta;
      } catch (error) {
        setStatus(
          `Duplicate sample failed: ${error instanceof Error ? error.message : String(error)}`,
        );
        return undefined;
      }
    },
    async exportSampleWav(sampleId) {
      try {
        await ensureSampleLibraryReady();
        const runtime = await resolveSampleRuntime(sampleId);
        if (!runtime?.pcm.length) {
          setStatus("Sample missing — cannot export");
          return;
        }
        const wav = encodeWavPcm16(runtime.pcm, runtime.sampleRate, 1);
        const safe = (runtime.name || "sample").replace(/[^\w\-]+/g, "_") || "sample";
        downloadWav(wav, `${safe}.wav`);
        setStatus(`Exported ${safe}.wav`);
      } catch (error) {
        setStatus(
          `Export WAV failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async importLibrarySamples(files, folderId): Promise<SampleAssetMeta[]> {
      const list = files.filter(file => file.size > 0);
      if (list.length === 0) {
        return [];
      }
      if (!engine) {
        await enableAudio();
      }
      const ctx = engine?.context;
      if (!ctx) {
        setStatus("Audio engine not ready");
        return [];
      }
      if (ctx.state === "suspended") {
        await ctx.resume();
      }
      const imported: SampleAssetMeta[] = [];
      let failed = 0;
      for (const file of list) {
        try {
          const bytes = await file.arrayBuffer();
          const buffer = await ctx.decodeAudioData(bytes.slice(0));
          const pcm = audioBufferToMono(buffer);
          const name = file.name.replace(/\.[^.]+$/, "") || "Sample";
          const meta = await ingestSamplePcm({
            name,
            pcm,
            sampleRate: buffer.sampleRate,
            source: "import",
            folderId: folderId ?? null,
          });
          putCachedSample({
            id: meta.id,
            name: meta.name,
            sampleRate: meta.sampleRate,
            pcm,
            rootKey: meta.rootKey,
          });
          sampleNameCache.set(meta.id, meta.name);
          imported.push(meta);
        } catch {
          failed += 1;
        }
      }
      const ok = imported.length;
      if (ok === 0) {
        setStatus(
          failed > 0
            ? `Import failed · ${failed} file${failed === 1 ? "" : "s"}`
            : "No audio files to import",
        );
        return imported;
      }
      if (ok === 1 && failed === 0) {
        setStatus(`Imported sample “${imported[0]!.name}”`);
        return imported;
      }
      setStatus(
        failed > 0
          ? `Imported ${ok} sample${ok === 1 ? "" : "s"} · ${failed} failed`
          : `Imported ${ok} samples`,
      );
      return imported;
    },
    async exportSampleLibrary() {
      try {
        await ensureSampleLibraryReady();
        const [assets, folders] = await Promise.all([
          listImportedSampleAssets(),
          listSampleFolders(),
        ]);
        const samples = await collectSamplesForExport(assets.map(a => a.id));
        const bytes = serializeAdamSamplesPackage({ folders, samples });
        downloadBytes(
          bytes,
          `library${ADAM_SAMPLES_FILE_EXTENSION}`,
          ADAM_SAMPLES_MIME_TYPE,
        );
        setStatus(
          `Exported .adamsamples · ${samples.length} sample${samples.length === 1 ? "" : "s"} · ${folders.length} folder${folders.length === 1 ? "" : "s"}`,
        );
      } catch (error) {
        setStatus(
          `Library export failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async importSampleLibrary() {
      try {
        const { bytes, name } = await pickAdamSamplesFile();
        const opened = deserializeAdamSamplesPackage(bytes);
        await ensureSampleLibraryReady();
        if (opened.folders.length > 0) {
          await ingestFoldersFromPackage(opened.folders);
        }
        for (const sample of opened.samples) {
          const meta = await ingestSampleFromPackage(sample.meta, sample.pcm);
          putCachedSample({
            id: meta.id,
            name: meta.name,
            sampleRate: meta.sampleRate,
            pcm: sample.pcm,
            rootKey: meta.rootKey,
            oneCycle: meta.oneCycle,
          });
          sampleNameCache.set(meta.id, meta.name);
        }
        setStatus(
          `Imported ${name} · ${opened.samples.length} sample${opened.samples.length === 1 ? "" : "s"} · ${opened.folders.length} folder${opened.folders.length === 1 ? "" : "s"} (merged)`,
        );
      } catch (error) {
        if (error instanceof Error && error.message === "No file selected") {
          return;
        }
        setStatus(
          `Library import failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async wipeSampleLibrary() {
      try {
        await ensureSampleLibraryReady();
        const { sampleCount, folderCount } = await wipeSampleLibraryCatalog();
        sampleNameCache.clear();
        setStatus(
          sampleCount === 0 && folderCount === 0
            ? "Sample library already empty"
            : `Wiped sample library · ${sampleCount} sample${sampleCount === 1 ? "" : "s"} · ${folderCount} folder${folderCount === 1 ? "" : "s"}`,
        );
      } catch (error) {
        setStatus(
          `Wipe failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async renderSamplerPadsToDrums(slot) {
      try {
        const instrument = projectStore.getValue().instruments[slot];
        if (instrument?.kind !== "sampler") {
          setStatus("Switch slot to Sampler to render pads");
          return;
        }
        if (projectStore.getValue().instruments.length >= MAX_INSTRUMENT_SLOT_COUNT) {
          setStatus(`Track limit reached (${MAX_INSTRUMENT_SLOT_COUNT}).`);
          return;
        }
        const preset = instrument.preset;
        const regions = listSamplerPadRegions(preset);
        if (regions.length === 0) {
          setStatus("No sampler pads to render — explode slices onto pads first");
          return;
        }

        await ensureSampleLibraryReady();
        const baseName = (preset.name || "Sampler").trim() || "Sampler";
        const folderName = `${baseName} pads`.slice(0, 48);
        const folder = createSampleFolderSync({ name: folderName });

        const assigned: Array<{ id: string; name: string }> = [];
        for (const region of regions) {
          const runtime = await resolveSampleRuntime(region.sampleId);
          if (!runtime?.pcm.length) {
            continue;
          }
          const lo = Math.max(0, Math.min(runtime.pcm.length - 1, region.start));
          const hiRaw = region.end > 0 ? region.end : runtime.pcm.length;
          const hi = Math.max(lo + 1, Math.min(runtime.pcm.length, hiRaw));
          const pcm = new Float32Array(hi - lo);
          pcm.set(runtime.pcm.subarray(lo, hi));
          const meta = await ingestSamplePcm({
            name: region.name,
            pcm,
            sampleRate: runtime.sampleRate,
            rootKey: runtime.rootKey ?? 60,
            source: "chop",
            folderId: folder.id,
          });
          putCachedSample({
            id: meta.id,
            name: meta.name,
            sampleRate: meta.sampleRate,
            pcm,
            rootKey: meta.rootKey,
          });
          sampleNameCache.set(meta.id, meta.name);
          assigned.push({ id: meta.id, name: meta.name });
        }

        if (assigned.length === 0) {
          setStatus("Render failed — pad sample data missing");
          return;
        }

        const kitName = `${baseName} kit`.slice(0, 48);
        let kit = createDefaultDrumKit();
        kit = { ...kit, name: kitName };
        kit = assignSamplesToDrumPads(kit, 0, assigned);

        const added = applyAddTrack();
        if (!added) {
          return;
        }
        const newSlot = added.slot as InstrumentSlot;
        commitInstrument(newSlot, drumsInstrument(kit));
        tracker.renameTrack(newSlot, kitName);

        const fromBank = structuredClone(projectStore.getValue().drumBank);
        const nextBank = sanitizeDrumBank([
          ...fromBank,
          {
            id: `user-kit-${Date.now().toString(36)}`,
            name: kitName,
            kit: structuredClone(kit),
          },
        ]);
        getAdamCommandHost().execute(new SetDrumBankCommand(fromBank, nextBank));

        try {
          if (engine) {
            await pushSamplesForInstruments(
              engine,
              [projectStore.getValue().instruments[newSlot]!],
              resolveSampleRuntime,
            );
          }
        } catch {
          // pads still usable once audio starts
        }

        openInstrument(newSlot);
        patchUi({ authorOpen: true });
        setStatus(
          `Rendered ${assigned.length} pad${assigned.length === 1 ? "" : "s"} → drums I${newSlot + 1} · folder “${folderName}” · kit “${kitName}”`,
        );
      } catch (error) {
        setStatus(
          `Render pads failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    async importSamplerAudio(slot, file) {
      if (!engine) {
        setStatus("Audio engine not ready");
        return;
      }
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind !== "sampler") {
        setStatus("Switch slot to Sampler to import audio");
        return;
      }
      const bytes = await file.arrayBuffer();
      const ctx = engine.context;
      if (!ctx) {
        setStatus("No audio context");
        return;
      }
      const buffer = await ctx.decodeAudioData(bytes.slice(0));
      const pcm = audioBufferToMono(buffer);
      await ingestDecodedSampler(slot, {
        name: file.name.replace(/\.[^.]+$/, "") || "Sample",
        pcm,
        sampleRate: buffer.sampleRate,
        source: "import",
      });
    },
    async ingestSamplerPcm(slot, options) {
      await ingestDecodedSampler(slot, options);
    },
    getAudioContext() {
      return engine?.context;
    },
    applyWavetableBankEntry(slot, bankId) {
      if (projectStore.getValue().instruments[slot]?.kind !== "wavetable") {
        setStatus("Switch slot to Wavetable to use the wavetable bank");
        return;
      }
      const result = applyWtBankEntry(wtBankState(), slot, bankId);
      if (result) {
        commitWtBankResult(result);
      }
    },
    saveWavetableBank(slot) {
      if (projectStore.getValue().instruments[slot]?.kind !== "wavetable") {
        setStatus("Switch slot to Wavetable to save presets");
        return;
      }
      const selected =
        projectStore.getValue().wavetableBank.find(
          entry =>
            entry.name
            === (projectStore.getValue().instruments[slot]?.kind === "wavetable"
              ? projectStore.getValue().instruments[slot].preset.name
              : ""),
        )?.id;
      const result = saveWtBankCurrent(wtBankState(), slot, selected);
      if (!result) {
        runSaveWtBankAs(slot);
        return;
      }
      commitWtBankResult(result);
    },
    saveWavetableBankAs(slot) {
      if (projectStore.getValue().instruments[slot]?.kind !== "wavetable") {
        setStatus("Switch slot to Wavetable to save presets");
        return;
      }
      runSaveWtBankAs(slot);
    },
    renameWavetableBank(bankId) {
      const entry = projectStore.getValue().wavetableBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const name = window.prompt("Rename preset", entry.name)?.trim();
      if (!name) {
        return;
      }
      const result = renameWtBankEntry(wtBankState(), bankId, name);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitWtBankResult(result);
    },
    deleteWavetableBank(bankId) {
      const entry = projectStore.getValue().wavetableBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      if (entry.builtin) {
        setStatus("Factory presets cannot be deleted");
        return;
      }
      if (!window.confirm(`Delete preset “${entry.name}”?`)) {
        return;
      }
      const result = deleteWtBankEntry(wtBankState(), bankId);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitWtBankResult(result);
    },
    exportWavetableBank(bankId) {
      const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
      const payload = buildWtExportEntryPayload(wtBankState(), bankId, slot);
      if (!payload) {
        setStatus("Nothing to export");
        return;
      }
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    exportAllWavetableBank() {
      const payload = buildWtExportAllPayload(wtBankState());
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    importWavetableBank() {
      void (async () => {
        try {
          const data = await pickJsonFile();
          const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
          const result = importWtBankData(wtBankState(), slot, data);
          commitWtBankResult(result);
        } catch (error) {
          if (error instanceof Error && error.message === "No file selected") {
            return;
          }
          setStatus(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
        }
      })();
    },
    editWavetableBankTags(bankId) {
      const entry = projectStore.getValue().wavetableBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const current = entry.tags?.join(", ") ?? "";
      const nextRaw = window.prompt("Tags (comma-separated, used for search)", current);
      if (nextRaw === null) {
        return;
      }
      const tags = nextRaw.split(/[,;]/).map(part => part.trim()).filter(Boolean);
      const result = setWtBankTags(wtBankState(), bankId, tags);
      if (result) {
        commitWtBankResult(result);
      }
    },
    setWavetableBankCategory(bankId, category) {
      const result = setWtBankCategory(wtBankState(), bankId, category);
      if (result) {
        commitWtBankResult(result);
      }
    },
    applySamplerBankEntry(slot, bankId) {
      if (projectStore.getValue().instruments[slot]?.kind !== "sampler") {
        setStatus("Switch slot to Sampler to use the sampler bank");
        return;
      }
      const result = applySamplerBankEntry(samplerBankState(), slot, bankId);
      if (result) {
        commitSamplerBankResult(result);
      }
    },
    saveSamplerBank(slot) {
      if (projectStore.getValue().instruments[slot]?.kind !== "sampler") {
        setStatus("Switch slot to Sampler to save presets");
        return;
      }
      const selected =
        projectStore.getValue().samplerBank.find(
          entry =>
            entry.name
            === (projectStore.getValue().instruments[slot]?.kind === "sampler"
              ? projectStore.getValue().instruments[slot].preset.name
              : ""),
        )?.id;
      const result = saveSamplerBankCurrent(samplerBankState(), slot, selected);
      if (!result) {
        runSaveSamplerBankAs(slot);
        return;
      }
      commitSamplerBankResult(result);
    },
    saveSamplerBankAs(slot) {
      if (projectStore.getValue().instruments[slot]?.kind !== "sampler") {
        setStatus("Switch slot to Sampler to save presets");
        return;
      }
      runSaveSamplerBankAs(slot);
    },
    renameSamplerBank(bankId) {
      const entry = projectStore.getValue().samplerBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const name = window.prompt("Rename preset", entry.name)?.trim();
      if (!name) {
        return;
      }
      const result = renameSamplerBankEntry(samplerBankState(), bankId, name);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitSamplerBankResult(result);
    },
    deleteSamplerBank(bankId) {
      const entry = projectStore.getValue().samplerBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      if (entry.builtin) {
        setStatus("Factory presets cannot be deleted");
        return;
      }
      if (!window.confirm(`Delete preset “${entry.name}”?`)) {
        return;
      }
      const result = deleteSamplerBankEntry(samplerBankState(), bankId);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitSamplerBankResult(result);
    },
    exportSamplerBank(bankId) {
      const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
      const payload = buildSamplerExportEntryPayload(samplerBankState(), bankId, slot);
      if (!payload) {
        setStatus("Nothing to export");
        return;
      }
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    exportAllSamplerBank() {
      const payload = buildSamplerExportAllPayload(samplerBankState());
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    importSamplerBank() {
      void (async () => {
        try {
          const data = await pickJsonFile();
          const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
          const result = importSamplerBankData(samplerBankState(), slot, data);
          commitSamplerBankResult(result);
        } catch (error) {
          if (error instanceof Error && error.message === "No file selected") {
            return;
          }
          setStatus(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
        }
      })();
    },
    editSamplerBankTags(bankId) {
      const entry = projectStore.getValue().samplerBank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const current = entry.tags?.join(", ") ?? "";
      const nextRaw = window.prompt("Tags (comma-separated, used for search)", current);
      if (nextRaw === null) {
        return;
      }
      const tags = nextRaw.split(/[,;]/).map(part => part.trim()).filter(Boolean);
      const result = setSamplerBankEntryTags(samplerBankState(), bankId, tags);
      if (result) {
        commitSamplerBankResult(result);
      }
    },
    setSamplerBankCategory(bankId, category) {
      const result = setSamplerBankEntryCategory(samplerBankState(), bankId, category);
      if (result) {
        commitSamplerBankResult(result);
      }
    },
    applyDrumBankEntry(slot, bankId) {
      const entry = projectStore.getValue().drumBank.find(e => e.id === bankId);
      if (!entry) {
        return;
      }
      commitInstrument(slot, drumsInstrument({ ...entry.kit, name: entry.name }));
      setStatus(`Loaded kit “${entry.name}” → slot ${slot + 1}`);
    },
    saveDrumBank(slot) {
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind !== "drums") {
        setStatus("Slot is not a drum kit");
        return;
      }
      const kit = instrument.kit;
      const fromBank = structuredClone(projectStore.getValue().drumBank);
      const drumBank = fromBank.map(e => ({ ...e, kit: structuredClone(e.kit) }));
      const existing = drumBank.find(e => e.name === kit.name && !e.builtin);
      if (existing) {
        existing.kit = structuredClone(kit);
        existing.name = kit.name;
        const next = sanitizeDrumBank(drumBank);
        getAdamCommandHost().execute(new SetDrumBankCommand(fromBank, next));
        setStatus(`Saved kit “${kit.name}”`);
        return;
      }
      const name = window.prompt("Save drum kit as", kit.name)?.trim();
      if (!name) {
        return;
      }
      drumBank.push({
        id: `user-kit-${Date.now().toString(36)}`,
        name,
        kit: { ...structuredClone(kit), name },
      });
      const next = sanitizeDrumBank(drumBank);
      const nextInstrument = drumsInstrument({ ...kit, name });
      getAdamCommandHost().execute(
        new AdamTransactionCommand("Save drum kit", [
          new SetDrumBankCommand(fromBank, next),
          new SetInstrumentCommand(slot, cloneInstrument(instrument), nextInstrument),
        ]),
      );
      setStatus(`Saved kit “${name}”`);
    },
    saveDrumBankAs(slot) {
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind !== "drums") {
        setStatus("Slot is not a drum kit");
        return;
      }
      const kit = instrument.kit;
      const name = window.prompt("Save drum kit as", kit.name)?.trim();
      if (!name) {
        return;
      }
      const fromBank = structuredClone(projectStore.getValue().drumBank);
      const next = sanitizeDrumBank([
        ...fromBank,
        {
          id: `user-kit-${Date.now().toString(36)}`,
          name,
          kit: { ...structuredClone(kit), name },
        },
      ]);
      const nextInstrument = drumsInstrument({ ...kit, name });
      getAdamCommandHost().execute(
        new AdamTransactionCommand("Save drum kit as", [
          new SetDrumBankCommand(fromBank, next),
          new SetInstrumentCommand(slot, cloneInstrument(instrument), nextInstrument),
        ]),
      );
      setStatus(`Saved kit “${name}”`);
    },
    instrumentNoteOn(slot, note, velocity) {
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind === "externalMidi") {
        const t = engine?.getCurrentTime() ?? 0;
        externalMidiSink.noteOn(slot, note, velocity, t);
        return;
      }
      engine?.transport.noteOn(slot, note, velocity);
    },
    instrumentNoteOff(slot, note) {
      const instrument = projectStore.getValue().instruments[slot];
      if (instrument?.kind === "externalMidi") {
        const t = engine?.getCurrentTime() ?? 0;
        externalMidiSink.noteOff(slot, note, t);
        return;
      }
      engine?.transport.noteOff(slot, note);
    },
    applyInstrumentBankEntry(slot, bankId) {
      if (projectStore.getValue().instruments[slot]?.kind === "drums") {
        setStatus("Switch slot to Synth to use the preset bank");
        return;
      }
      const result = applyBankEntry(bankState(), slot, bankId);
      if (result) {
        commitBankResult(result);
      }
    },
    saveInstrumentBank(slot) {
      if (projectStore.getValue().instruments[slot]?.kind === "drums") {
        setStatus("Switch slot to Synth to save presets");
        return;
      }
      const selected =
        projectStore.getValue().bank.find(
          entry => entry.name === projectStore.getValue().presets[slot]?.name,
        )?.id;
      const result = saveBankCurrent(bankState(), slot, selected);
      if (!result) {
        runSaveBankAs(slot);
        return;
      }
      commitBankResult(result);
    },
    saveInstrumentBankAs(slot) {
      if (projectStore.getValue().instruments[slot]?.kind === "drums") {
        setStatus("Switch slot to Synth to save presets");
        return;
      }
      runSaveBankAs(slot);
    },
    renameInstrumentBank(bankId) {
      const entry = projectStore.getValue().bank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const name = window.prompt("Rename preset", entry.name)?.trim();
      if (!name) {
        return;
      }
      const result = renameBankEntry(bankState(), bankId, name);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitBankResult(result);
    },
    deleteInstrumentBank(bankId) {
      const entry = projectStore.getValue().bank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      if (entry.builtin) {
        setStatus("Factory presets cannot be deleted");
        return;
      }
      if (!window.confirm(`Delete preset “${entry.name}”?`)) {
        return;
      }
      const result = deleteBankEntry(bankState(), bankId);
      if (!result) {
        return;
      }
      if ("statusOnly" in result) {
        setStatus(result.statusOnly);
        return;
      }
      commitBankResult(result);
    },
    exportInstrumentBank(bankId) {
      const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
      const payload = buildExportEntryPayload(bankState(), bankId, slot);
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    exportAllInstrumentBank() {
      const payload = buildExportAllPayload(bankState());
      downloadJson(payload.json, payload.filename);
      setStatus(payload.status);
    },
    importInstrumentBank() {
      void (async () => {
        try {
          const data = await pickJsonFile();
          const slot = (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot;
          const result = importBankData(bankState(), slot, data);
          commitBankResult(result);
        } catch (error) {
          if (error instanceof Error && error.message === "No file selected") {
            return;
          }
          setStatus(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
        }
      })();
    },
    editInstrumentBankTags(bankId) {
      const entry = projectStore.getValue().bank.find(item => item.id === bankId);
      if (!entry) {
        return;
      }
      const current = entry.tags?.join(", ") ?? "";
      const nextRaw = window.prompt("Tags (comma-separated, used for search)", current);
      if (nextRaw === null) {
        return;
      }
      const tags = nextRaw.split(/[,;]/).map(part => part.trim()).filter(Boolean);
      const result = setBankTags(bankState(), bankId, tags);
      if (result) {
        commitBankResult(result);
      }
    },
    setInstrumentBankCategory(bankId, category) {
      const result = setBankCategory(bankState(), bankId, category);
      if (result) {
        commitBankResult(result);
      }
    },
  });

  function runSaveBankAs(slot: InstrumentSlot): void {
    const suggested = projectStore.getValue().presets[slot]?.name || "Untitled";
    const name = window.prompt("Save preset as", suggested)?.trim();
    if (!name) {
      return;
    }
    let result = saveBankAs(bankState(), slot, name, false);
    if (result && "conflict" in result) {
      const overwrite = window.confirm(`A preset named “${name}” already exists. Overwrite it?`);
      if (!overwrite) {
        return;
      }
      result = saveBankAs(bankState(), slot, name, true);
    }
    if (!result || "conflict" in result) {
      return;
    }
    commitBankResult(result);
  }

  function runSaveWtBankAs(slot: InstrumentSlot): void {
    const instrument = projectStore.getValue().instruments[slot];
    const suggested =
      instrument?.kind === "wavetable" ? instrument.preset.name : "Untitled";
    const name = window.prompt("Save preset as", suggested)?.trim();
    if (!name) {
      return;
    }
    let result = saveWtBankAs(wtBankState(), slot, name, false);
    if (result && "conflict" in result) {
      const overwrite = window.confirm(`A preset named “${name}” already exists. Overwrite it?`);
      if (!overwrite) {
        return;
      }
      result = saveWtBankAs(wtBankState(), slot, name, true);
    }
    if (!result || "conflict" in result) {
      return;
    }
    commitWtBankResult(result);
  }

  function runSaveSamplerBankAs(slot: InstrumentSlot): void {
    const instrument = projectStore.getValue().instruments[slot];
    const suggested =
      instrument?.kind === "sampler" ? instrument.preset.name : "Untitled";
    const name = window.prompt("Save preset as", suggested)?.trim();
    if (!name) {
      return;
    }
    let result = saveSamplerBankAs(samplerBankState(), slot, name, false);
    if (result && "conflict" in result) {
      const overwrite = window.confirm(`A preset named “${name}” already exists. Overwrite it?`);
      if (!overwrite) {
        return;
      }
      result = saveSamplerBankAs(samplerBankState(), slot, name, true);
    }
    if (!result || "conflict" in result) {
      return;
    }
    commitSamplerBankResult(result);
  }
  // Apply initial material without going through the labActions wrapper.
  setMaterialMode(saved.material, { persist: false });

  const chipFresh = (LAB_DEMOS.find(demo => demo.id === "chip") ?? LAB_DEMOS[0]!).load();
  const looksLikeDefaultChip =
    saved.pattern === stringifyPattern(chipFresh.clip)
    && saved.fx.length === chipFresh.fx.length
    && saved.fx.every((node, i) => node.type === chipFresh.fx[i]?.type)
    && saved.presets.length === chipFresh.presets.length
    && saved.presets.every((preset, i) => preset.name === chipFresh.presets[i]?.name)
    && (saved.slotFx?.length ?? 0) === (chipFresh.slotFx?.length ?? 0)
    && saved.bank.length <= Math.max(4, chipFresh.bank?.length ?? 0)
    && !(saved.instruments ?? []).some(inst => inst.kind === "drums");

  setStatus(looksLikeDefaultChip ? "Engine not started." : "Restored lab state from localStorage.");

  startControlSession();
  setControlBindingsPersistence(bindings => {
    persist.schedule({ controlBindings: bindings });
  });
  setControlBindings(saved.controlBindings ?? []);
  setMidiAuditionSlotGetter(
    () => (uiStore.getValue().instrumentSlot ?? lastInstrumentSlot) as InstrumentSlot,
  );
  setMidiAuditionVoiceModeGetter(slot => {
    const kind = projectStore.getValue().instruments[slot]?.kind ?? "synth";
    // Match MusicalKeyboard: SF2 / drums / sampler are poly; synth / wavetable are mono.
    // Match MusicalKeyboard: SF2 / drums / sampler / external MIDI are poly; synth / wavetable are mono.
    return kind === "sf2" || kind === "drums" || kind === "sampler" || kind === "externalMidi"
      ? "poly"
      : "mono";
  });
  startMidiNoteAudition();

  const handle: LabRuntimeHandle = {
    dispose() {
      if (activeRuntime !== handle) {
        return;
      }
      persist.flush();
      stopMidiNoteAudition();
      stopControlSession();
      window.removeEventListener("keydown", onSpacePlayKey, true);
      window.removeEventListener("keydown", onShiftEnterJumpKey, true);
      window.removeEventListener("keydown", onToggleArmKey, true);
      window.removeEventListener("keydown", onArmEscapeKey, true);
      window.removeEventListener("keydown", onTogglePanelKey, true);
      window.removeEventListener("keydown", onOpenAuthorKey, true);
      window.removeEventListener("keydown", onUndoRedoKey, true);
      window.removeEventListener("keydown", onDoubleShiftGoToKey, true);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      discardTransportMic();
      if (performanceBus) {
        performanceBus.stop();
        performanceBus = null;
        patchPerformance({ recording: false, playing: false });
      }
      performancePlaying = false;
      registerAdamProjectBridge(null);
      registerAdamArrangeBridge(null);
      registerAdamInstrumentBridge(null);
      registerAdamSampleBridge(null);
      clearAdamSessionHistory();
      session?.dispose();
      engine?.dispose();
      externalMidiSink.dispose();
      session = undefined;
      engine = undefined;
      setLabActions(null);
      activeRuntime = null;
    },
  };
  activeRuntime = handle;
  return handle;
}
